<template>
  <HrPageLayout
    title="Announcements"
    subtitle="Company communications and news"
    icon="📢"
    :breadcrumb-items="[{ label: 'Company Communications' }, { label: 'Announcements' }]"
  >

    <div class="hr-announcements-container">

      <!-- ── Top Toolbar ── -->
      <div class="ann-toolbar">
        <!-- Left: view tabs -->
        <div class="ann-view-tabs">
          <button
            @click="currentView = 'active'; getAnnouncements()"
            class="ann-tab"
            :class="{ 'ann-tab-active': currentView === 'active' }"
          >
            <i class="bx bx-bullhorn"></i>
            <span>Active</span>
            <span v-if="metrics.activeAnnouncements" class="ann-tab-count">{{ metrics.activeAnnouncements }}</span>
          </button>
          <button
            @click="currentView = 'archived'; getAnnouncements()"
            class="ann-tab"
            :class="{ 'ann-tab-active': currentView === 'archived' }"
          >
            <i class="bx bx-archive"></i>
            <span>Archived</span>
            <span v-if="metrics.archivedAnnouncements" class="ann-tab-count ann-tab-count-muted">{{ metrics.archivedAnnouncements }}</span>
          </button>
        </div>

        <!-- Right: action buttons -->
        <div class="ann-toolbar-actions">
          <button @click="refreshData" class="ann-tool-btn ann-tool-refresh" title="Refresh">
            <i class="bx bx-refresh"></i>
            <span>Refresh</span>
          </button>
          <button @click="exportAnnouncements" class="ann-tool-btn ann-tool-export" title="Export">
            <i class="bx bx-download"></i>
            <span>Export</span>
          </button>
          <button @click="createAnnouncement" class="ann-tool-btn ann-tool-create">
            <i class="bx bx-plus"></i>
            <span>New Announcement</span>
          </button>
        </div>
      </div>

      <!-- Filters and controls section -->
      <div class="hr-chart-card hr-mb-4">
        <div class="hr-chart-body hr-py-3">
          <div class="hr-filters-grid">
            <div class="hr-filter-group">
              <label class="hr-form-label">Announcement Type</label>
              <select v-model="filters.type" @change="getAnnouncements" class="hr-select">
                <option value="">All Types</option>
                <option v-for="type in announcementTypes" :key="type" :value="type">
                  {{ type }}
                </option>
              </select>
            </div>

            <div class="hr-filter-group">
              <label class="hr-form-label">Priority</label>
              <select v-model="filters.priority" @change="getAnnouncements" class="hr-select">
                <option value="">All Priorities</option>
                <option value="High">High</option>
                <option value="Medium">Medium</option>
                <option value="Low">Low</option>
              </select>
            </div>

            <div class="hr-filter-group" v-if="currentView === 'active'">
              <label class="hr-form-label">Status</label>
              <select v-model="filters.status" @change="getAnnouncements" class="hr-select">
                <option value="">All Statuses</option>
                <option value="Active">Active</option>
                <option value="Draft">Draft</option>
              </select>
            </div>

            <div class="hr-filter-group">
              <label class="hr-form-label">Department</label>
              <select v-model="filters.departmentId" @change="getAnnouncements" class="hr-select">
                <option value="">All Departments</option>
                <option v-for="dept in departments" :key="dept.id" :value="dept.id">
                  {{ dept.departmentName }}
                </option>
              </select>
            </div>

            <div class="hr-filter-group">
              <label class="hr-form-label">Date Range</label>
              <div class="hr-date-range">
                <input v-model="filters.startDate" @change="getAnnouncements" type="date" class="hr-input">
                <span class="hr-date-separator">to</span>
                <input v-model="filters.endDate" @change="getAnnouncements" type="date" class="hr-input">
              </div>
            </div>

            <div class="hr-filter-group">
              <label class="hr-form-label">Search</label>
              <div class="hr-search-input">
                <input
                  type="text"
                  v-model="filters.search"
                  @input="debounceSearch"
                  class="hr-input"
                  placeholder="Search by title, content..."
                >
                <i class="bx bx-search hr-search-icon"></i>
              </div>
            </div>

            <div class="hr-filter-actions">
              <button @click="resetFilters" class="hr-btn hr-btn-outline">
                <i class="bx bx-refresh"></i> Reset
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Statistics cards -->
      <div class="hr-metrics-grid hr-mb-4" v-if="currentView === 'active'">
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-bullhorn"></i></div>
          <p class="hr-metric-label">Total Announcements</p>
          <h2 class="hr-metric-value">{{ metrics.totalAnnouncements || 0 }}</h2>
          <span class="hr-metric-change">All time</span>
        </div>

        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-bullhorn-outline"></i></div>
          <p class="hr-metric-label">Active Announcements</p>
          <h2 class="hr-metric-value">{{ metrics.activeAnnouncements || 0 }}</h2>
          <span class="hr-metric-change hr-text-success">Currently published</span>
        </div>

        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-error-circle"></i></div>
          <p class="hr-metric-label">High Priority</p>
          <h2 class="hr-metric-value">{{ metrics.highPriority || 0 }}</h2>
          <span class="hr-metric-change hr-text-danger">Important announcements</span>
        </div>

        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon"><i class="bx bx-check-circle"></i></div>
          <p class="hr-metric-label">Require Acknowledgment</p>
          <h2 class="hr-metric-value">{{ metrics.requireAcknowledgment || 0 }}</h2>
          <span class="hr-metric-change hr-text-info">Employee confirmation needed</span>
        </div>
      </div>

      <!-- Archived Statistics -->
      <div class="hr-metrics-grid hr-mb-4" v-if="currentView === 'archived'">
        <div class="hr-metric-card gradient-1">
          <div class="hr-metric-icon"><i class="bx bx-archive"></i></div>
          <p class="hr-metric-label">Archived Announcements</p>
          <h2 class="hr-metric-value">{{ metrics.archivedAnnouncements || 0 }}</h2>
          <span class="hr-metric-change">Total archived</span>
        </div>

        <div class="hr-metric-card gradient-2">
          <div class="hr-metric-icon"><i class="bx bx-calendar"></i></div>
          <p class="hr-metric-label">This Month</p>
          <h2 class="hr-metric-value">{{ archivedThisMonth || 0 }}</h2>
          <span class="hr-metric-change">Archived this month</span>
        </div>

        <div class="hr-metric-card gradient-3">
          <div class="hr-metric-icon"><i class="bx bx-time"></i></div>
          <p class="hr-metric-label">This Year</p>
          <h2 class="hr-metric-value">{{ archivedThisYear || 0 }}</h2>
          <span class="hr-metric-change">Archived this year</span>
        </div>

        <div class="hr-metric-card gradient-4">
          <div class="hr-metric-icon"><i class="bx bx-show"></i></div>
          <p class="hr-metric-label">Total Views</p>
          <h2 class="hr-metric-value">{{ totalArchivedViews || 0 }}</h2>
          <span class="hr-metric-change">Views on archived</span>
        </div>
      </div>

      <!-- Announcements List -->
      <div class="hr-chart-card">
        <div class="hr-chart-header">
          <h5 class="hr-chart-title">
            {{ currentView === 'active' ? 'Active Announcements' : 'Archived Announcements' }}
          </h5>
          <div class="hr-chart-actions">
            <span class="hr-text-muted">{{ announcements.length }} of {{ pagination.totalAnnouncements }} announcements</span>
          </div>
        </div>
        <div class="hr-chart-body">
          <!-- Loading overlay -->
          <div v-if="loading" class="hr-loading-wrap hr-py-5">
            <div class="hr-spinner"></div>
            <p class="hr-text-muted hr-mt-2">Loading announcements...</p>
          </div>

          <!-- Empty state -->
          <div v-else-if="!announcements.length" class="hr-empty-state hr-py-5">
            <i class="bx bx-bullhorn-outline hr-empty-icon"></i>
            <h5 class="hr-empty-title">No announcements found</h5>
            <p class="hr-empty-text">
              {{ currentView === 'active' 
                ? 'Try adjusting your filters or create a new announcement' 
                : 'No archived announcements match your criteria' }}
            </p>
            <button v-if="currentView === 'active'" @click="resetFilters" class="hr-btn hr-btn-outline hr-btn-primary">
              Reset Filters
            </button>
          </div>

          <!-- Announcements Cards -->
          <div v-else class="ann-list">
            <div v-for="announcement in announcements" :key="announcement.id" class="ann-item">
              <div class="ann-item-accent" :class="'ann-accent-' + (announcement.priority || 'low').toLowerCase()"></div>
              <div class="ann-item-body">
                <div class="ann-item-top">
                  <div class="ann-item-icon" :class="getAnnouncementTypeClass(announcement.announcement_type)">
                    <i class="bx bx-bullhorn"></i>
                  </div>
                  <div class="ann-item-main">
                    <div class="ann-item-title-row">
                      <h6 class="ann-item-title">{{ announcement.title }}</h6>
                      <div class="ann-item-badges">
                        <span class="ann-badge ann-badge-type" :class="getAnnouncementTypeClass(announcement.announcement_type)">{{ announcement.announcement_type }}</span>
                        <span class="ann-badge" :class="getPriorityBadgeClass(announcement.priority)"><i class="bx bx-flag"></i> {{ announcement.priority }}</span>
                        <span class="ann-badge" :class="getStatusBadgeClass(announcement.status)">{{ announcement.status }}</span>
                        <span v-if="announcement.require_acknowledgment" class="ann-badge ann-badge-ack"><i class="bx bx-check-shield"></i> Ack. Required</span>
                      </div>
                    </div>
                    <p class="ann-item-excerpt">{{ truncateText(announcement.content, 140) }}</p>
                    <div class="ann-item-meta">
                      <span><i class="bx bx-calendar"></i> {{ formatDate(announcement.publish_date) }}</span>
                      <span v-if="announcement.expiry_date"><i class="bx bx-calendar-x"></i> Expires {{ formatDate(announcement.expiry_date) }}</span>
                      <span><i class="bx bx-user"></i> {{ announcement.created_by }}</span>
                      <span><i class="bx bx-show"></i> {{ announcement.views || 0 }} views</span>
                    </div>
                  </div>
                  <div class="ann-item-actions">
                    <button @click="viewAnnouncementDetails(announcement)" class="ann-action-btn ann-action-view" title="View Details">
                      <i class="bx bx-show"></i><span>View</span>
                    </button>
                    <button v-if="canEditAnnouncement(announcement)" @click="editAnnouncement(announcement)" class="ann-action-btn ann-action-edit" title="Edit">
                      <i class="bx bx-edit"></i><span>Edit</span>
                    </button>
                    <button v-if="announcement.status === 'Active' && announcement.require_acknowledgment" @click="viewAcknowledgmentStats(announcement)" class="ann-action-btn ann-action-stats" title="Acknowledgment Stats">
                      <i class="bx bx-bar-chart-alt-2"></i><span>Stats</span>
                    </button>
                    <button v-if="announcement.status === 'Active' && currentView === 'active'" @click="archiveAnnouncement(announcement)" class="ann-action-btn ann-action-archive" title="Archive">
                      <i class="bx bx-archive"></i><span>Archive</span>
                    </button>
                    <button v-if="currentView === 'archived'" @click="restoreAnnouncement(announcement)" class="ann-action-btn ann-action-restore" title="Restore">
                      <i class="bx bx-undo"></i><span>Restore</span>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Pagination -->
          <div v-if="announcements.length && pagination.totalPages > 1" class="hr-pagination-wrap hr-mt-4">
            <div class="hr-pagination-info">
              Showing {{ (pagination.currentPage - 1) * pagination.limit + 1 }} to 
              {{ Math.min(pagination.currentPage * pagination.limit, pagination.totalAnnouncements) }}
              of {{ pagination.totalAnnouncements }} announcements
            </div>
            <nav class="hr-pagination">
              <button type="button" class="hr-page-btn" :disabled="pagination.currentPage === 1" 
                      @click="changePage(pagination.currentPage - 1)">
                <i class="bx bx-chevron-left"></i>
              </button>
              <button v-for="page in getPageNumbers()" :key="page" type="button" class="hr-page-btn"
                      :class="{ 'hr-page-active': pagination.currentPage === page, 'hr-page-disabled': page === '...' }"
                      @click="page !== '...' && changePage(page)">
                {{ page }}
              </button>
              <button type="button" class="hr-page-btn" :disabled="pagination.currentPage === pagination.totalPages"
                      @click="changePage(pagination.currentPage + 1)">
                <i class="bx bx-chevron-right"></i>
              </button>
            </nav>
          </div>
        </div>
      </div>
    </div>

  <!-- Announcement Create/Edit Modal -->

  </HrPageLayout>

  <!-- Teleport modals to body to avoid stacking context issues -->
  <Teleport to="body">

  <!-- Announcement Details Modal -->
  <div class="modal fade" id="announcementDetailsModal" tabindex="-1" aria-labelledby="announcementDetailsModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-xl modal-dialog-scrollable">
      <div class="modal-content ann-modal-content">
        <!-- Modern modal header with gradient -->
        <div class="ann-modal-header">
          <div class="ann-modal-header-inner" v-if="selectedAnnouncement">
            <div class="ann-modal-title-wrap">
              <div class="ann-modal-icon" :class="getAnnouncementTypeClass(selectedAnnouncement.announcement_type)">
                <i class="bx bx-bullhorn"></i>
              </div>
              <div>
                <h4 class="ann-modal-title">{{ selectedAnnouncement.title }}</h4>
                <div class="ann-modal-badges">
                  <span class="ann-badge ann-badge-type" :class="getAnnouncementTypeClass(selectedAnnouncement.announcement_type)">{{ selectedAnnouncement.announcement_type }}</span>
                  <span class="ann-badge" :class="getPriorityBadgeClass(selectedAnnouncement.priority)"><i class="bx bx-flag"></i> {{ selectedAnnouncement.priority }}</span>
                  <span class="ann-badge" :class="getStatusBadgeClass(selectedAnnouncement.status)">{{ selectedAnnouncement.status }}</span>
                </div>
              </div>
            </div>
            <button type="button" class="ann-modal-close" @click="hideModalById('announcementDetailsModal')" aria-label="Close">
              <i class="bx bx-x"></i>
            </button>
          </div>
          <div v-else class="ann-modal-header-inner">
            <h4 class="ann-modal-title">Announcement Details</h4>
            <button type="button" class="ann-modal-close" @click="hideModalById('announcementDetailsModal')" aria-label="Close">
              <i class="bx bx-x"></i>
            </button>
          </div>
        </div>

        <div class="modal-body ann-modal-body" v-if="selectedAnnouncement">
          <!-- Meta info strip -->
          <div class="ann-detail-meta-strip">
            <div class="ann-detail-meta-item">
              <i class="bx bx-user-circle"></i>
              <div><span class="ann-meta-label">Posted by</span><span class="ann-meta-value">{{ selectedAnnouncement.created_by }}</span></div>
            </div>
            <div class="ann-detail-meta-item">
              <i class="bx bx-calendar-check"></i>
              <div><span class="ann-meta-label">Published</span><span class="ann-meta-value">{{ formatDate(selectedAnnouncement.publish_date) }}</span></div>
            </div>
            <div class="ann-detail-meta-item" v-if="selectedAnnouncement.expiry_date">
              <i class="bx bx-calendar-x"></i>
              <div><span class="ann-meta-label">Expires</span><span class="ann-meta-value">{{ formatDate(selectedAnnouncement.expiry_date) }}</span></div>
            </div>
            <div class="ann-detail-meta-item">
              <i class="bx bx-show"></i>
              <div><span class="ann-meta-label">Views</span><span class="ann-meta-value">{{ selectedAnnouncement.views || 0 }}</span></div>
            </div>
            <div class="ann-detail-meta-item">
              <i class="bx bx-time-five"></i>
              <div><span class="ann-meta-label">Last Updated</span><span class="ann-meta-value">{{ formatDateTime(selectedAnnouncement.updated_at || selectedAnnouncement.created_at) }}</span></div>
            </div>
          </div>

          <!-- Content -->
          <div class="ann-detail-section">
            <div class="ann-detail-section-header"><i class="bx bx-file-blank"></i> Announcement Content</div>
            <div class="ann-detail-content-body" v-html="formatAnnouncementContent(selectedAnnouncement.content)"></div>
          </div>

          <!-- Target Audience + Attachments row -->
          <div class="ann-detail-row">
            <div class="ann-detail-section ann-detail-half">
              <div class="ann-detail-section-header"><i class="bx bx-group"></i> Target Audience</div>
              <div class="ann-detail-section-body">
                <p class="ann-audience-label">{{ selectedAnnouncement.target_audience }}</p>
                <div v-if="selectedAnnouncement.departmentsList && selectedAnnouncement.departmentsList.length > 0" class="ann-dept-tags">
                  <span v-for="deptId in selectedAnnouncement.departmentsList" :key="deptId" class="ann-dept-tag">
                    <i class="bx bx-buildings"></i> {{ getDepartmentName(deptId) }}
                  </span>
                </div>
                <p v-if="selectedAnnouncement.require_acknowledgment" class="ann-ack-notice">
                  <i class="bx bx-check-shield"></i> Requires employee acknowledgment
                </p>
              </div>
            </div>
            <div class="ann-detail-section ann-detail-half">
              <div class="ann-detail-section-header"><i class="bx bx-paperclip"></i> Attachments</div>
              <div class="ann-detail-section-body">
                <div v-if="selectedAnnouncement.attachmentsList && selectedAnnouncement.attachmentsList.length > 0" class="ann-attach-list">
                  <div v-for="(att, idx) in selectedAnnouncement.attachmentsList" :key="idx" class="ann-attach-item">
                    <div class="ann-attach-icon"><i class="bx bx-file"></i></div>
                    <div class="ann-attach-info">
                      <span class="ann-attach-name">{{ att.name }}</span>
                      <span class="ann-attach-size">{{ formatFileSize(att.size) }}</span>
                    </div>
                    <a :href="att.path" class="ann-attach-download" download title="Download"><i class="bx bx-download"></i></a>
                  </div>
                </div>
                <div v-else class="ann-empty-small"><i class="bx bx-file-blank"></i><span>No attachments</span></div>
              </div>
            </div>
          </div>

          <!-- Comments -->
          <div class="ann-detail-section">
            <div class="ann-detail-section-header">
              <span><i class="bx bx-comment-dots"></i> Comments</span>
              <span class="ann-count-badge">{{ selectedAnnouncement.comments ? selectedAnnouncement.comments.length : 0 }}</span>
            </div>
            <div class="ann-detail-section-body">
              <div v-if="selectedAnnouncement.comments && selectedAnnouncement.comments.length > 0" class="ann-comments">
                <div v-for="comment in selectedAnnouncement.comments" :key="comment.id" class="ann-comment">
                  <div class="ann-comment-avatar">{{ getInitials(comment.employee_name) }}</div>
                  <div class="ann-comment-body">
                    <div class="ann-comment-header">
                      <span class="ann-comment-author">{{ comment.employee_name }}</span>
                      <span class="ann-comment-time">{{ formatDateTime(comment.created_at) }}</span>
                      <button v-if="canDeleteComment(comment)" @click="deleteComment(comment.id)" class="ann-comment-delete" title="Delete"><i class="bx bx-trash"></i></button>
                    </div>
                    <p class="ann-comment-text">{{ comment.comment }}</p>
                  </div>
                </div>
              </div>
              <div v-else class="ann-empty-small"><i class="bx bx-comment"></i><span>No comments yet</span></div>
              <div v-if="selectedAnnouncement.status === 'Active'" class="ann-comment-form">
                <textarea v-model="commentForm.comment" class="ann-comment-input" rows="2" placeholder="Write a comment..."></textarea>
                <button class="ann-comment-post" @click="addComment" :disabled="!commentForm.comment.trim()">
                  <i class="bx bx-send"></i> Post
                </button>
              </div>
            </div>
          </div>

          <!-- Activity Timeline -->
          <div v-if="selectedAnnouncement.activities && selectedAnnouncement.activities.length > 0" class="ann-detail-section">
            <div class="ann-detail-section-header"><i class="bx bx-history"></i> Activity History</div>
            <div class="ann-detail-section-body">
              <div class="ann-timeline">
                <div v-for="(activity, idx) in selectedAnnouncement.activities" :key="idx" class="ann-timeline-item">
                  <div class="ann-timeline-dot" :class="getActivityTypeClass(activity.activity_type)">
                    <i :class="['bx', getActivityTypeIcon(activity.activity_type)]"></i>
                  </div>
                  <div class="ann-timeline-content">
                    <div class="ann-timeline-header">
                      <span class="ann-timeline-title">{{ activity.title }}</span>
                      <span class="ann-timeline-time">{{ formatDateTime(activity.timestamp) }}</span>
                    </div>
                    <p class="ann-timeline-desc">{{ activity.description }}</p>
                    <span class="ann-timeline-by">By {{ activity.user }}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <!-- Acknowledgments -->
          <div v-if="selectedAnnouncement.require_acknowledgment" class="ann-detail-section">
            <div class="ann-detail-section-header">
              <span><i class="bx bx-check-circle"></i> Acknowledgments</span>
              <span class="ann-count-badge ann-count-success">{{ selectedAnnouncement.acknowledgments ? selectedAnnouncement.acknowledgments.length : 0 }}</span>
            </div>
            <div class="ann-detail-section-body">
              <div v-if="selectedAnnouncement.acknowledgments && selectedAnnouncement.acknowledgments.length > 0" class="table-responsive">
                <table class="ann-table">
                  <thead><tr><th>Employee</th><th>Acknowledged At</th><th>Feedback</th></tr></thead>
                  <tbody>
                    <tr v-for="ack in selectedAnnouncement.acknowledgments" :key="ack.id">
                      <td>{{ ack.employee_name }}</td>
                      <td>{{ formatDateTime(ack.acknowledged_at) }}</td>
                      <td>{{ ack.feedback || '—' }}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div v-else class="ann-empty-small"><i class="bx bx-check-circle"></i><span>No acknowledgments yet</span></div>
            </div>
          </div>
        </div>

        <!-- Modern footer with properly placed buttons -->
        <div class="ann-modal-footer">
          <button type="button" class="ann-footer-btn ann-footer-close" @click="hideModalById('announcementDetailsModal')">
            <i class="bx bx-x"></i> Close
          </button>
          <div class="ann-footer-right">
            <button v-if="selectedAnnouncement && canEditAnnouncement(selectedAnnouncement)" type="button" class="ann-footer-btn ann-footer-edit" @click="editAnnouncement(selectedAnnouncement)">
              <i class="bx bx-edit"></i> Edit Announcement
            </button>
            <button v-if="selectedAnnouncement && !hasAcknowledged && selectedAnnouncement.require_acknowledgment && selectedAnnouncement.status === 'Active'" type="button" class="ann-footer-btn ann-footer-ack" @click="acknowledgeAnnouncement">
              <i class="bx bx-check-double"></i> Acknowledge
            </button>
          </div>
        </div>
      </div>
    </div>
  </div>



  <!-- Announcement Create/Edit Modal -->
  <div class="modal fade" id="announcementModal" tabindex="-1" aria-labelledby="announcementModalLabel" aria-hidden="true" data-bs-backdrop="false" data-bs-keyboard="true">
    <div class="modal-dialog modal-xl modal-dialog-scrollable">
      <div class="modal-content ann-form-modal">

        <!-- Header -->
        <div class="ann-form-header">
          <div class="ann-form-header-inner">
            <div class="ann-form-header-icon">
              <i class="bx" :class="isEditMode ? 'bx-edit' : 'bx-plus-circle'"></i>
            </div>
            <div>
              <h4 class="ann-form-title">{{ isEditMode ? 'Edit Announcement' : 'Create New Announcement' }}</h4>
              <p class="ann-form-subtitle">{{ isEditMode ? 'Update the announcement details below' : 'Fill in the details to publish a new announcement' }}</p>
            </div>
          </div>
          <button type="button" class="ann-modal-close" @click="closeAnnouncementModal" aria-label="Close">
            <i class="bx bx-x"></i>
          </button>
        </div>

        <!-- Body -->
        <div class="modal-body ann-form-body">
          <form id="announcementForm" @submit.prevent="submitAnnouncement">

            <!-- Section: Basic Info -->
            <div class="ann-form-section">
              <div class="ann-form-section-label"><i class="bx bx-info-circle"></i> Basic Information</div>
              <div class="ann-form-grid">

                <!-- Title full width -->
                <div class="ann-field ann-field-full">
                  <label class="ann-label">Title <span class="ann-required">*</span></label>
                  <input v-model="announcementForm.title" type="text" class="ann-input" placeholder="Enter announcement title..." required>
                </div>

                <!-- Type -->
                <div class="ann-field">
                  <label class="ann-label">Type <span class="ann-required">*</span></label>
                  <div class="ann-select-wrap">
                    <i class="bx bx-category ann-select-icon"></i>
                    <select v-model="announcementForm.announcementType" class="ann-select" required>
                      <option value="" disabled>Select type...</option>
                      <option v-for="type in announcementTypes" :key="type" :value="type">{{ type }}</option>
                    </select>
                  </div>
                </div>

                <!-- Priority -->
                <div class="ann-field">
                  <label class="ann-label">Priority <span class="ann-required">*</span></label>
                  <div class="ann-priority-group">
                    <label class="ann-priority-opt" :class="{ 'ann-priority-high': announcementForm.priority === 'High' }">
                      <input type="radio" v-model="announcementForm.priority" value="High" hidden>
                      <i class="bx bx-error-circle"></i> High
                    </label>
                    <label class="ann-priority-opt" :class="{ 'ann-priority-medium': announcementForm.priority === 'Medium' }">
                      <input type="radio" v-model="announcementForm.priority" value="Medium" hidden>
                      <i class="bx bx-minus-circle"></i> Medium
                    </label>
                    <label class="ann-priority-opt" :class="{ 'ann-priority-low': announcementForm.priority === 'Low' }">
                      <input type="radio" v-model="announcementForm.priority" value="Low" hidden>
                      <i class="bx bx-down-arrow-circle"></i> Low
                    </label>
                  </div>
                </div>

                <!-- Publish Date -->
                <div class="ann-field">
                  <label class="ann-label">Publish Date <span class="ann-required">*</span></label>
                  <div class="ann-input-icon-wrap">
                    <i class="bx bx-calendar-check ann-input-icon"></i>
                    <input v-model="announcementForm.publishDate" type="date" class="ann-input ann-input-icon-pad" required>
                  </div>
                </div>

                <!-- Expiry Date -->
                <div class="ann-field">
                  <label class="ann-label">Expiry Date <span class="ann-hint">(optional)</span></label>
                  <div class="ann-input-icon-wrap">
                    <i class="bx bx-calendar-x ann-input-icon"></i>
                    <input v-model="announcementForm.expiryDate" type="date" class="ann-input ann-input-icon-pad">
                  </div>
                  <span class="ann-help">Leave blank if it doesn't expire</span>
                </div>

              </div>
            </div>

            <!-- Section: Audience -->
            <div class="ann-form-section">
              <div class="ann-form-section-label"><i class="bx bx-group"></i> Target Audience</div>
              <div class="ann-audience-group">
                <label class="ann-audience-opt" :class="{ 'ann-audience-active': announcementForm.targetAudience === 'All Employees' }">
                  <input type="radio" v-model="announcementForm.targetAudience" value="All Employees" hidden>
                  <div class="ann-audience-icon"><i class="bx bx-world"></i></div>
                  <div>
                    <span class="ann-audience-title">All Employees</span>
                    <span class="ann-audience-desc">Visible to everyone in the organisation</span>
                  </div>
                  <i class="bx bx-check-circle ann-audience-check"></i>
                </label>
                <label class="ann-audience-opt" :class="{ 'ann-audience-active': announcementForm.targetAudience === 'Specific Departments' }">
                  <input type="radio" v-model="announcementForm.targetAudience" value="Specific Departments" hidden>
                  <div class="ann-audience-icon"><i class="bx bx-buildings"></i></div>
                  <div>
                    <span class="ann-audience-title">Specific Departments</span>
                    <span class="ann-audience-desc">Target selected departments only</span>
                  </div>
                  <i class="bx bx-check-circle ann-audience-check"></i>
                </label>
              </div>

              <!-- Department checkboxes -->
              <div v-if="announcementForm.targetAudience === 'Specific Departments'" class="ann-dept-grid">
                <label v-for="dept in departments" :key="dept.id" class="ann-dept-check" :class="{ 'ann-dept-checked': announcementForm.departments.includes(dept.id) }">
                  <input type="checkbox" :value="dept.id" v-model="announcementForm.departments" hidden>
                  <i class="bx" :class="announcementForm.departments.includes(dept.id) ? 'bx-check-square' : 'bx-square'"></i>
                  {{ dept.departmentName }}
                </label>
              </div>
            </div>

            <!-- Section: Content -->
            <div class="ann-form-section">
              <div class="ann-form-section-label"><i class="bx bx-file-blank"></i> Content <span class="ann-required">*</span></div>
              <textarea v-model="announcementForm.content" class="ann-textarea" rows="10" placeholder="Write your announcement content here..." required></textarea>
              <span class="ann-help">Plain text. Use line breaks to separate paragraphs.</span>
            </div>

            <!-- Section: Settings -->
            <div class="ann-form-section">
              <div class="ann-form-section-label"><i class="bx bx-cog"></i> Settings</div>
              <div class="ann-form-grid">

                <!-- Status -->
                <div class="ann-field">
                  <label class="ann-label">Publish Status <span class="ann-required">*</span></label>
                  <div class="ann-status-group">
                    <label class="ann-status-opt" :class="{ 'ann-status-active': announcementForm.status === 'Active' }">
                      <input type="radio" v-model="announcementForm.status" value="Active" hidden>
                      <i class="bx bx-broadcast"></i>
                      <span>Publish Now</span>
                    </label>
                    <label class="ann-status-opt" :class="{ 'ann-status-draft': announcementForm.status === 'Draft' }">
                      <input type="radio" v-model="announcementForm.status" value="Draft" hidden>
                      <i class="bx bx-save"></i>
                      <span>Save as Draft</span>
                    </label>
                  </div>
                </div>

                <!-- Acknowledgment toggle -->
                <div class="ann-field">
                  <label class="ann-label">Acknowledgment</label>
                  <label class="ann-toggle-wrap" :class="{ 'ann-toggle-on': announcementForm.requireAcknowledgment }">
                    <div class="ann-toggle-info">
                      <i class="bx bx-check-shield"></i>
                      <div>
                        <span class="ann-toggle-title">Require Acknowledgment</span>
                        <span class="ann-toggle-desc">Employees must confirm they've read this</span>
                      </div>
                    </div>
                    <div class="ann-toggle-switch" @click="announcementForm.requireAcknowledgment = !announcementForm.requireAcknowledgment">
                      <div class="ann-toggle-knob"></div>
                    </div>
                  </label>
                </div>

              </div>
            </div>

            <!-- Section: Attachments -->
            <div class="ann-form-section">
              <div class="ann-form-section-label"><i class="bx bx-paperclip"></i> Attachments <span class="ann-hint">(optional)</span></div>
              <label class="ann-file-drop">
                <input type="file" multiple ref="attachmentFiles" @change="handleFileSelection" hidden>
                <i class="bx bx-cloud-upload ann-file-icon"></i>
                <span class="ann-file-text">Click to upload files</span>
                <span class="ann-file-hint">Documents, images, PDFs</span>
              </label>

              <div v-if="pendingFiles && pendingFiles.length" class="ann-existing-files">
                <div v-for="(file, idx) in pendingFiles" :key="'pending-' + idx" class="ann-file-item">
                  <div class="ann-file-item-icon"><i class="bx bx-file"></i></div>
                  <span class="ann-file-item-name">{{ file.name }}</span>
                  <button type="button" class="ann-file-remove" @click="removePendingFile(idx)" title="Remove">
                    <i class="bx bx-x"></i>
                  </button>
                </div>
              </div>

              <!-- Existing attachments -->
              <div v-if="announcementForm.attachmentsList && announcementForm.attachmentsList.length > 0" class="ann-existing-files">
                <div v-for="(att, idx) in announcementForm.attachmentsList" :key="idx" class="ann-file-item">
                  <div class="ann-file-item-icon"><i class="bx bx-file"></i></div>
                  <span class="ann-file-item-name">{{ att.name }}</span>
                  <button type="button" class="ann-file-remove" @click="removeAttachment(att, idx)" title="Remove">
                    <i class="bx bx-x"></i>
                  </button>
                </div>
              </div>
            </div>

          </form>
        </div>

        <!-- Footer -->
        <div class="ann-form-footer">
          <button type="button" class="ann-footer-btn ann-footer-close" @click="closeAnnouncementModal">
            <i class="bx bx-x"></i> Cancel
          </button>
          <button type="submit" form="announcementForm" class="ann-footer-btn ann-form-submit" :disabled="submitLoading">
            <span v-if="submitLoading" class="ann-btn-spinner"></span>
            <i v-else class="bx" :class="isEditMode ? 'bx-save' : 'bx-send'"></i>
            {{ submitLoading ? 'Saving...' : (isEditMode ? 'Update Announcement' : 'Publish Announcement') }}
          </button>
        </div>

      </div>
    </div>
  </div>

  <!-- Acknowledgment Modal -->
  <div class="modal fade" id="acknowledgeModal" tabindex="-1" aria-labelledby="acknowledgeModalLabel" aria-hidden="true">
    <div class="modal-dialog">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="acknowledgeModalLabel">Acknowledge Announcement</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" @click="closeModalFromEvent($event)"></button>
        </div>
        <div class="modal-body">
          <div v-if="selectedAnnouncement">
            <p>You are acknowledging the following announcement:</p>
            <div class="alert alert-info">
              <h5 class="alert-heading">{{ selectedAnnouncement.title }}</h5>
              <p class="mb-0"><strong>Type:</strong> {{ selectedAnnouncement.announcement_type }}</p>
              <p class="mb-0"><strong>Published:</strong> {{ formatDate(selectedAnnouncement.publish_date) }}</p>
            </div>
            <div class="mb-3">
              <label class="form-label">Feedback (Optional)</label>
              <textarea v-model="acknowledgeForm.feedback" class="form-control" rows="4" placeholder="You may provide optional feedback about this announcement..."></textarea>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="closeModalFromEvent($event)">Cancel</button>
          <button type="button" class="btn btn-success" @click="confirmAcknowledgeAnnouncement">
            <i class="mdi mdi-check me-1"></i> Acknowledge Announcement
          </button>
        </div>
      </div>
    </div>
  </div>

  <!-- Acknowledgment Stats Modal -->
  <div class="modal fade" id="acknowledgeStatsModal" tabindex="-1" aria-labelledby="acknowledgeStatsModalLabel" aria-hidden="true">
    <div class="modal-dialog modal-lg">
      <div class="modal-content">
        <div class="modal-header">
          <h5 class="modal-title" id="acknowledgeStatsModalLabel">Acknowledgment Statistics</h5>
          <button type="button" class="btn-close" data-bs-dismiss="modal" aria-label="Close" @click="closeModalFromEvent($event)"></button>
        </div>
        <div class="modal-body">
          <div v-if="selectedAnnouncement">
            <h5 class="mb-3">{{ selectedAnnouncement.title }}</h5>

            <!-- Overall Stats -->
            <div class="row mb-4">
              <div class="col-md-6 mb-3">
                <div class="card">
                  <div class="card-body">
                    <h6 class="card-title mb-3">Acknowledgment Progress</h6>
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <span>Acknowledged</span>
                      <span class="text-success">{{ ackStats.acknowledgedCount }} / {{ ackStats.totalEmployees }}</span>
                    </div>
                    <div class="progress mb-3" style="height: 10px;">
                      <div class="progress-bar bg-success" role="progressbar" :style="`width: ${ackStats.acknowledgedPercentage}%`" :aria-valuenow="ackStats.acknowledgedPercentage" aria-valuemin="0" aria-valuemax="100">
                        {{ ackStats.acknowledgedPercentage }}%
                      </div>
                    </div>
                    <div class="d-flex justify-content-between align-items-center mb-2">
                      <span>Pending</span>
                      <span class="text-warning">{{ ackStats.pendingCount }} / {{ ackStats.totalEmployees }}</span>
                    </div>
                    <div class="progress" style="height: 10px;">
                      <div class="progress-bar bg-warning" role="progressbar" :style="`width: ${ackStats.pendingPercentage}%`" :aria-valuenow="ackStats.pendingPercentage" aria-valuemin="0" aria-valuemax="100">
                        {{ ackStats.pendingPercentage }}%
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div class="col-md-6 mb-3">
                <div class="card">
                  <div class="card-body">
                    <h6 class="card-title mb-3">Summary</h6>
                    <ul class="list-group list-group-flush">
                      <li class="list-group-item d-flex justify-content-between align-items-center px-0">
                        Total Employees
                        <span class="badge bg-primary rounded-pill">{{ ackStats.totalEmployees }}</span>
                      </li>
                      <li class="list-group-item d-flex justify-content-between align-items-center px-0">
                        Acknowledged
                        <span class="badge bg-success rounded-pill">{{ ackStats.acknowledgedCount }}</span>
                      </li>
                      <li class="list-group-item d-flex justify-content-between align-items-center px-0">
                        Pending
                        <span class="badge bg-warning rounded-pill">{{ ackStats.pendingCount }}</span>
                      </li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>

            <!-- Department Stats -->
            <div v-if="ackStats.departmentStats && ackStats.departmentStats.length > 0" class="card mb-4">
              <div class="card-header bg-transparent">
                <h6 class="card-title mb-0">Department Breakdown</h6>
              </div>
              <div class="card-body">
                <div class="table-responsive">
                  <table class="table table-hover">
                    <thead class="table-light">
                    <tr>
                      <th>Department</th>
                      <th class="text-center">Employees</th>
                      <th class="text-center">Acknowledged</th>
                      <th class="text-center">Pending</th>
                      <th class="text-center">Progress</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="dept in ackStats.departmentStats" :key="dept.id">
                      <td>{{ dept.name }}</td>
                      <td class="text-center">{{ dept.totalEmployees }}</td>
                      <td class="text-center">{{ dept.acknowledged }}</td>
                      <td class="text-center">{{ dept.pending }}</td>
                      <td>
                        <div class="progress" style="height: 8px;">
                          <div class="progress-bar bg-success" role="progressbar" :style="`width: ${dept.percentage}%`" :aria-valuenow="dept.percentage" aria-valuemin="0" aria-valuemax="100"></div>
                        </div>
                        <small class="d-block text-center mt-1">{{ dept.percentage }}%</small>
                      </td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            <!-- Recent Acknowledgments -->
            <div v-if="ackStats.recentAcknowledgments && ackStats.recentAcknowledgments.length > 0" class="card">
              <div class="card-header bg-transparent">
                <h6 class="card-title mb-0">Recent Acknowledgments</h6>
              </div>
              <div class="card-body">
                <div class="table-responsive">
                  <table class="table table-hover">
                    <thead class="table-light">
                    <tr>
                      <th>Employee</th>
                      <th>Acknowledged At</th>
                      <th>Feedback</th>
                    </tr>
                    </thead>
                    <tbody>
                    <tr v-for="ack in ackStats.recentAcknowledgments" :key="ack.id">
                      <td>{{ ack.employee_name }}</td>
                      <td>{{ formatDateTime(ack.acknowledged_at) }}</td>
                      <td>{{ ack.feedback || 'No feedback provided' }}</td>
                    </tr>
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          </div>
        </div>
        <div class="modal-footer">
          <button type="button" class="btn btn-secondary" data-bs-dismiss="modal" @click="closeModalFromEvent($event)">Close</button>
          <button type="button" class="btn btn-primary" @click="exportAcknowledgmentStats">
            <i class="mdi mdi-file-export me-1"></i> Export Statistics
          </button>
        </div>
      </div>
    </div>
  </div>

  </Teleport>
</template>

<script>
/* eslint-disable */
import { toast, authUrl, execute } from "@/api";
import _ from 'lodash';
import HrPageLayout from "@/components/hrActions/HrPageLayout.vue";
import { jsPDF } from "jspdf";
import autoTable from 'jspdf-autotable';
import * as bootstrap from "bootstrap";

export default {
  name: "Announcements",
  components: { HrPageLayout },
  data() {
    return {
      submitLoading: false,
      loading: false,
      currentView: 'active', // 'active' or 'archived'
      searchTimeout: null,
      announcements: [],
      recentAnnouncements: [],
      departments: [],
      announcementTypes: [
        'General',
        'Policy Update',
        'Event',
        'Emergency',
        'HR Announcement',
        'IT Notice',
        'Training',
        'Holiday',
        'Maintenance',
        'Other'
      ],
      filters: {
        departmentId: "",
        type: "",
        priority: "",
        status: "",
        startDate: this.getDateOffset(-90), // Last 90 days
        endDate: this.getDateOffset(365), // Next year
        search: "",
        page: 1,
        limit: 10
      },
      pagination: {
        currentPage: 1,
        totalPages: 1,
        totalAnnouncements: 0,
        limit: 10
      },
      metrics: {
        totalAnnouncements: 0,
        highPriority: 0,
        mediumPriority: 0,
        lowPriority: 0,
        activeAnnouncements: 0,
        draftAnnouncements: 0,
        archivedAnnouncements: 0,
        requireAcknowledgment: 0,
        publishedToday: 0,
        expiringThisWeek: 0,
        typeCounts: {}
      },
      selectedAnnouncement: null,
      announcementForm: {
        title: "",
        announcementType: "General",
        content: "",
        publishDate: new Date().toISOString().split('T')[0],
        expiryDate: "",
        priority: "Medium",
        status: "Active",
        targetAudience: "All Employees",
        departments: [],
        requireAcknowledgment: false,
        attachmentsList: []
      },
      isEditMode: false,
      removedAttachments: [],
      pendingFiles: [],
      acknowledgeForm: {
        feedback: ""
      },
      commentForm: {
        comment: ""
      },
      hasAcknowledged: false,
      ackStats: {
        totalEmployees: 0,
        acknowledgedCount: 0,
        pendingCount: 0,
        acknowledgedPercentage: 0,
        pendingPercentage: 0,
        departmentStats: [],
        recentAcknowledgments: []
      }
    };
  },
  computed: {
    archivedThisMonth() {
      // Calculate archived announcements this month
      return this.announcements.filter(a => {
        if (a.status !== 'Archived') return false;
        const archivedDate = new Date(a.updated_at);
        const now = new Date();
        return archivedDate.getMonth() === now.getMonth() && 
               archivedDate.getFullYear() === now.getFullYear();
      }).length;
    },
    archivedThisYear() {
      // Calculate archived announcements this year
      return this.announcements.filter(a => {
        if (a.status !== 'Archived') return false;
        const archivedDate = new Date(a.updated_at);
        const now = new Date();
        return archivedDate.getFullYear() === now.getFullYear();
      }).length;
    },
    totalArchivedViews() {
      // Calculate total views for archived announcements
      return this.announcements
        .filter(a => a.status === 'Archived')
        .reduce((total, a) => total + (parseInt(a.views) || 0), 0);
    }
  },
  mounted() {
    this.getDepartments();
    this.getAnnouncements();
    this.getAnnouncementTypes();
  },
  beforeUnmount() {
    this.cleanupModalArtifacts();
  },
  methods: {
    getDateOffset(days) {
      const date = new Date();
      date.setDate(date.getDate() + days);
      return date.toISOString().split('T')[0];
    },

    getDepartments() {
      const data = new FormData();
      data.append("function", "getDepartments");
      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.departments = res.data.data.departments || [];
            } else {
              console.error("Error fetching departments:", res.data.message);
              this.showToast("error", "Failed to load departments");
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching departments");
          });
    },

    getAnnouncementTypes() {
      const data = new FormData();
      data.append("function", "getAnnouncementTypes");

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.announcementTypes = res.data.data.announcementTypes || this.announcementTypes;
            } else {
              console.error("Error fetching announcement types:", res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
          });
    },

    getAnnouncements() {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getAnnouncements");
      data.append("startDate", this.filters.startDate);
      data.append("endDate", this.filters.endDate);
      data.append("page", this.filters.page);
      data.append("limit", this.filters.limit);

      // Set status based on current view
      if (this.currentView === 'archived') {
        data.append("status", "Archived");
      } else {
        // For active view, use the status filter or default to non-archived
        if (this.filters.status) {
          data.append("status", this.filters.status);
        } else {
          // Don't include archived in active view
          data.append("excludeStatus", "Archived");
        }
      }

      if (this.filters.departmentId) {
        data.append("departmentId", this.filters.departmentId);
      }

      if (this.filters.type) {
        data.append("type", this.filters.type);
      }

      if (this.filters.priority) {
        data.append("priority", this.filters.priority);
      }

      if (this.filters.search) {
        data.append("search", this.filters.search);
      }

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data.success) {
              this.announcements = res.data.data.announcements || [];
              this.metrics = res.data.data.metrics || {};
              this.recentAnnouncements = res.data.data.recentAnnouncements || [];
              this.pagination = res.data.data.pagination || {
                currentPage: 1,
                totalPages: 1,
                totalAnnouncements: 0,
                limit: 10
              };
            } else {
              console.error("Error fetching announcements:", res.data.message);
              this.showToast("error", "Failed to load announcements data: " + res.data.message);
              this.announcements = [];
              this.metrics = {};
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching announcements");
            this.announcements = [];
            this.metrics = {};
          });
    },

    refreshData() {
      this.getAnnouncements();
    },

    resetFilters() {
      this.filters = {
        departmentId: "",
        type: "",
        priority: "",
        status: "",
        startDate: this.getDateOffset(-90),
        endDate: this.getDateOffset(365),
        search: "",
        page: 1,
        limit: 10
      };
      this.getAnnouncements();
    },

    debounceSearch() {
      if (this.searchTimeout) {
        clearTimeout(this.searchTimeout);
      }
      this.searchTimeout = setTimeout(() => {
        this.filters.page = 1;
        this.getAnnouncements();
      }, 500);
    },

    restoreAnnouncement(announcement) {
      if (confirm(`Are you sure you want to restore the announcement "${announcement.title}"?`)) {
        const data = new FormData();
        data.append("function", "restoreAnnouncement");
        data.append("announcementId", announcement.id);

        execute(data, authUrl)
            .then((res) => {
              if (res.data.success) {
                this.showToast("success", res.data.message);
                this.getAnnouncements(); // Refresh the data
              } else {
                this.showToast("error", res.data.message);
              }
            })
            .catch((error) => {
              console.error("API error:", error);
              this.showToast("error", "Network error when restoring announcement");
            });
      }
    },

    getAnnouncementTypeClass(type) {
      const typeClasses = {
        'General': 'hr-badge-primary',
        'Policy Update': 'hr-badge-info',
        'Event': 'hr-badge-success',
        'Emergency': 'hr-badge-danger',
        'HR Announcement': 'hr-badge-info',
        'IT Notice': 'hr-badge-secondary',
        'Training': 'hr-badge-warning',
        'Holiday': 'hr-badge-success',
        'Maintenance': 'hr-badge-warning',
        'Other': 'hr-badge-secondary'
      };
      return typeClasses[type] || 'hr-badge-primary';
    },

    changePage(page) {
      if (page < 1 || page > this.pagination.totalPages || page === this.filters.page) {
        return;
      }

      this.filters.page = page;
      this.getAnnouncements();
    },

    getPageNumbers() {
      const totalPages = this.pagination.totalPages || 1;
      const currentPage = this.pagination.currentPage || 1;

      if (totalPages <= 5) {
        return Array.from({ length: totalPages }, (_, i) => i + 1);
      }

      const pages = [1];

      if (currentPage <= 3) {
        pages.push(2, 3);
        if (totalPages > 3) pages.push('...', totalPages);
      } else if (currentPage >= totalPages - 2) {
        pages.push('...', totalPages - 2, totalPages - 1, totalPages);
      } else {
        pages.push('...', currentPage - 1, currentPage, currentPage + 1, '...', totalPages);
      }

      return pages;
    },

    cleanupModalArtifacts() {
      document.body.classList.remove("modal-open");
      document.body.style.removeProperty("overflow");
      document.body.style.removeProperty("padding-right");
      document.querySelectorAll(".modal-backdrop").forEach((el) => el.remove());
    },

    forceShowModalElement(modalEl) {
      if (!modalEl) return;
      const isAnnouncementModal = modalEl.id === "announcementModal";
      modalEl.classList.add("show");
      modalEl.style.display = "block";
      modalEl.removeAttribute("aria-hidden");
      modalEl.setAttribute("aria-modal", "true");
      if (!isAnnouncementModal && !document.querySelector(".modal-backdrop")) {
        const bd = document.createElement("div");
        bd.className = "modal-backdrop fade show";
        document.body.appendChild(bd);
      }
      if (!isAnnouncementModal) {
        document.body.classList.add("modal-open");
        document.body.style.overflow = "hidden";
      }
    },

    forceHideModalElement(modalEl) {
      if (!modalEl) return;
      modalEl.classList.remove("show");
      modalEl.style.display = "none";
      modalEl.setAttribute("aria-hidden", "true");
      modalEl.removeAttribute("aria-modal");
    },

    hideModalById(modalId) {
      const modalEl = document.getElementById(modalId);
      if (!modalEl) {
        this.cleanupModalArtifacts();
        return;
      }
      try {
        const modal = bootstrap.Modal.getOrCreateInstance(modalEl);
        modal.hide();
      } catch (_) {
        this.forceHideModalElement(modalEl);
      }
      // Defensive cleanup in case backdrop remains due interrupted modal lifecycle.
      setTimeout(() => this.cleanupModalArtifacts(), 50);
    },

    showModalById(modalId) {
      const modalEl = document.getElementById(modalId);
      if (!modalEl) return;
      try {
        // Clear any stale backdrop/lock state before opening a modal.
        this.cleanupModalArtifacts();
        const options = modalId === "announcementModal" ? { backdrop: false, keyboard: true } : {};
        const modal = bootstrap.Modal.getOrCreateInstance(modalEl, options);
        modal.show();
        // Fallback: if bootstrap did not show modal, force it visible.
        setTimeout(() => {
          const visible = modalEl.classList.contains("show") && modalEl.style.display !== "none";
          if (!visible) this.forceShowModalElement(modalEl);
        }, 30);
      } catch (e) {
        console.error("Failed to open modal:", e);
        this.cleanupModalArtifacts();
        this.forceShowModalElement(modalEl);
      }
    },

    closeModalFromEvent(event) {
      const modalEl = event?.target?.closest?.(".modal");
      if (modalEl?.id) {
        this.hideModalById(modalEl.id);
      } else {
        this.cleanupModalArtifacts();
      }
    },

    closeAnnouncementModal() {
      this.hideModalById('announcementModal');
    },

    createAnnouncement() {
      this.isEditMode = false;
      this.announcementForm = {
        title: "",
        announcementType: "General",
        content: "",
        publishDate: new Date().toISOString().split('T')[0],
        expiryDate: "",
        priority: "Medium",
        status: "Active",
        targetAudience: "All Employees",
        departments: [],
        requireAcknowledgment: false,
        attachmentsList: []
      };

      this.removedAttachments = [];
      this.pendingFiles = [];

      this.showModalById('announcementModal');
    },

    editAnnouncement(announcement) {
      this.isEditMode = true;
      this.selectedAnnouncement = announcement;

      // Close the details modal if it's open
      this.hideModalById('announcementDetailsModal');

      // Prepare form data
      this.announcementForm = {
        announcementId: announcement.id,
        title: announcement.title,
        announcementType: announcement.announcement_type,
        content: announcement.content,
        publishDate: announcement.publish_date,
        expiryDate: announcement.expiry_date || "",
        priority: announcement.priority,
        status: announcement.status,
        targetAudience: announcement.target_audience,
        departments: announcement.departmentsList || [],
        requireAcknowledgment: Boolean(announcement.require_acknowledgment),
        attachmentsList: announcement.attachmentsList || []
      };

      this.removedAttachments = [];
      this.pendingFiles = [];

      // Open the modal after a brief timeout to ensure the details modal has closed
      setTimeout(() => {
        this.showModalById('announcementModal');
      }, 500);
    },

    handleFileSelection(event) {
      const list = event?.target?.files ? Array.from(event.target.files) : [];
      this.pendingFiles = list;
    },
    removePendingFile(index) {
      const next = Array.isArray(this.pendingFiles) ? [...this.pendingFiles] : [];
      next.splice(index, 1);
      this.pendingFiles = next;
      if (this.$refs.attachmentFiles) {
        this.$refs.attachmentFiles.value = '';
      }
    },

    removeAttachment(attachment, index) {
      this.removedAttachments.push(attachment.path);
      this.announcementForm.attachmentsList.splice(index, 1);
    },


// Improved submitAnnouncement method with comprehensive error handling and debugging
    submitAnnouncement() {
      console.log("submitAnnouncement method called");

      // Validate form
      if (!this.validateAnnouncementForm()) {
        console.log("Form validation failed");
        return;
      }

      console.log("Form validation successful, preparing to submit");
      this.submitLoading = true;
      const data = new FormData();

      try {
        if (this.isEditMode) {
          data.append("function", "updateAnnouncement");
          data.append("announcementId", this.announcementForm.announcementId);
          console.log("Update mode - editing announcement ID:", this.announcementForm.announcementId);
        } else {
          data.append("function", "createAnnouncement");
          console.log("Create mode - creating new announcement");
        }

        // Add all form fields
        data.append("title", this.announcementForm.title);
        data.append("announcementType", this.announcementForm.announcementType);
        data.append("content", this.announcementForm.content);
        data.append("publishDate", this.announcementForm.publishDate);
        data.append("priority", this.announcementForm.priority);
        data.append("status", this.announcementForm.status);
        data.append("targetAudience", this.announcementForm.targetAudience);
        data.append("requireAcknowledgment", this.announcementForm.requireAcknowledgment ? 1 : 0);

        if (this.announcementForm.expiryDate) {
          data.append("expiryDate", this.announcementForm.expiryDate);
        }

        // Handle departments
        if (this.announcementForm.targetAudience === 'Specific Departments' && this.announcementForm.departments.length > 0) {
          data.append("departments", JSON.stringify(this.announcementForm.departments));
          console.log("Added departments:", this.announcementForm.departments);
        }

        // Handle file uploads
        if (this.pendingFiles && this.pendingFiles.length > 0) {
          console.log("Adding", this.pendingFiles.length, "file attachments");
          for (let i = 0; i < this.pendingFiles.length; i++) {
            data.append(`attachment_${i}`, this.pendingFiles[i]);
          }
        }

        // Handle attachment removals
        if (this.isEditMode && this.removedAttachments.length > 0) {
          data.append("removedAttachments", JSON.stringify(this.removedAttachments));
          console.log("Removed attachments:", this.removedAttachments);
        }

        console.log("Form data prepared, attempting to execute API call");

        // Check if execute function exists
        if (typeof execute !== 'function') {
          throw new Error("Execute function is not available!");
        }

        // Log the form data for debugging
        console.log("FormData contents:");
        for (let pair of data.entries()) {
          console.log(pair[0] + ': ' + (pair[0].includes('attachment') ? '[File]' : pair[1]));
        }

        // Submit the form
        execute(data, authUrl)
            .then((res) => {
              console.log("API response received:", res.data);
              this.submitLoading = false;

              if (res.data.success) {
                // Close the modal
                console.log("Closing modal");
                this.hideModalById('announcementModal');

                this.showToast("success", res.data.message || "Announcement saved successfully");
                this.getAnnouncements(); // Refresh the data

                // Reset form data
                this.resetAnnouncementForm();

                // If we just created a new active announcement, show a success message
                if (!this.isEditMode && this.announcementForm.status === 'Active') {
                  this.showToast("info", "Announcement has been published successfully!");
                }
              } else {
                console.error("API returned error:", res.data.message);
                this.showToast("error", res.data.message || "Failed to save announcement");
              }
            })
            .catch((error) => {
              console.error("API call failed:", error);
              this.submitLoading = false;
              this.showToast("error", "Network error when saving announcement: " + (error.message || "Unknown error"));
            });
      } catch (error) {
        console.error("Error in form submission process:", error);
        this.submitLoading = false;
        this.showToast("error", "Error preparing form data: " + error.message);
      }
    },

// Helper method to reset the form
    resetAnnouncementForm() {
      this.announcementForm = {
        title: "",
        announcementType: "General",
        content: "",
        publishDate: new Date().toISOString().split('T')[0],
        expiryDate: "",
        priority: "Medium",
        status: "Active",
        targetAudience: "All Employees",
        departments: [],
        requireAcknowledgment: false,
        attachmentsList: []
      };
      this.pendingFiles = [];
      this.removedAttachments = [];

      // Reset file input if it exists
      if (this.$refs.attachmentFiles) {
        this.$refs.attachmentFiles.value = '';
      }
    },

// Enhanced validation method
    validateAnnouncementForm() {
      const errors = [];

      // Title validation
      if (!this.announcementForm.title.trim()) {
        errors.push("Title is required");
      }

      // Content validation
      if (!this.announcementForm.content.trim()) {
        errors.push("Content is required");
      }

      // Type validation
      if (!this.announcementForm.announcementType) {
        errors.push("Announcement type is required");
      }

      // Date validation
      if (!this.announcementForm.publishDate) {
        errors.push("Publish date is required");
      }

      // Expiry date check
      if (this.announcementForm.expiryDate &&
          new Date(this.announcementForm.expiryDate) <= new Date(this.announcementForm.publishDate)) {
        errors.push("Expiry date must be after publish date");
      }

      // Department validation
      if (this.announcementForm.targetAudience === 'Specific Departments' &&
          (!this.announcementForm.departments || this.announcementForm.departments.length === 0)) {
        errors.push("Please select at least one department");
      }

      // If there are errors, show them and return false
      if (errors.length > 0) {
        console.error("Form validation errors:", errors);
        this.showToast("error", errors[0]); // Show the first error
        return false;
      }

      // All validations passed
      return true;
    },


    viewAnnouncementDetails(announcement) {
      this.getAnnouncementDetails(announcement.id);
    },

    viewAcknowledgmentStats(announcement) {
      this.selectedAnnouncement = announcement;
      this.getAcknowledgmentStats(announcement.id);
    },

    acknowledgeAnnouncement() {
      this.acknowledgeForm = {
        feedback: ""
      };

      // Close the details modal
      this.hideModalById('announcementDetailsModal');

      // Show acknowledgment modal
      setTimeout(() => this.showModalById('acknowledgeModal'), 500);
    },

    archiveAnnouncement(announcement) {
      if (confirm(`Are you sure you want to archive the announcement "${announcement.title}"?`)) {
        const data = new FormData();
        data.append("function", "archiveAnnouncement");
        data.append("announcementId", announcement.id);

        execute(data, authUrl)
            .then((res) => {
              if (res.data.success) {
                this.showToast("success", res.data.message);
                this.getAnnouncements(); // Refresh the data
              } else {
                this.showToast("error", res.data.message);
              }
            })
            .catch((error) => {
              console.error("API error:", error);
              this.showToast("error", "Network error when archiving announcement");
            });
      }
    },

    getAnnouncementDetails(announcementId) {
      this.loading = true;

      const data = new FormData();
      data.append("function", "getAnnouncementDetails");
      data.append("announcementId", announcementId);

      execute(data, authUrl)
          .then((res) => {
            this.loading = false;

            if (res.data.success) {
              this.selectedAnnouncement = res.data.data.announcement;

              // Open the modal
              this.showModalById('announcementDetailsModal');

              // Check if the current user has acknowledged this announcement
              this.hasAcknowledged = false;
              if (this.selectedAnnouncement.acknowledgments) {
                // Get current user's ID
                const token = localStorage.getItem('token');
                const payload = JSON.parse(atob(token.split('.')[1]));
                const userId = payload.username;

                this.hasAcknowledged = this.selectedAnnouncement.acknowledgments.some(ack =>
                    ack.employee_id === userId
                );
              }

              // Reset comment form
              this.commentForm.comment = "";

          /*    // Open the modal
              this.showModalById('announcementDetailsModal');
*/

            } else {
              this.showToast("error", res.data.message);
            }
          })
          .catch((error) => {
            this.loading = false;
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching announcement details");
          });
    },

    confirmAcknowledgeAnnouncement() {
      const data = new FormData();
      data.append("function", "acknowledgeAnnouncement");
      data.append("announcementId", this.selectedAnnouncement.id);
      data.append("feedback", this.acknowledgeForm.feedback);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              // Close the acknowledgment modal
              this.hideModalById('acknowledgeModal');

              this.showToast("success", res.data.message);

              // Update the announcement details
              this.getAnnouncementDetails(this.selectedAnnouncement.id);
            } else {
              this.showToast("error", res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when acknowledging announcement");
          });
    },

    addComment() {
      if (!this.commentForm.comment.trim()) {
        this.showToast("error", "Comment cannot be empty");
        return;
      }

      const data = new FormData();
      data.append("function", "addComment");
      data.append("announcementId", this.selectedAnnouncement.id);
      data.append("comment", this.commentForm.comment);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.showToast("success", res.data.message);

              // Add the new comment to the list
              if (!this.selectedAnnouncement.comments) {
                this.selectedAnnouncement.comments = [];
              }

              this.selectedAnnouncement.comments.push({
                id: res.data.data.commentId,
                announcement_id: this.selectedAnnouncement.id,
                employee_id: "current_user", // This will be replaced when we refresh
                employee_name: res.data.data.employeeName,
                comment: this.commentForm.comment,
                created_at: res.data.data.created_at,
                status: 'Active'
              });

              // Clear the comment form
              this.commentForm.comment = "";
            } else {
              this.showToast("error", res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when adding comment");
          });
    },

    deleteComment(commentId) {
      if (confirm("Are you sure you want to delete this comment?")) {
        const data = new FormData();
        data.append("function", "deleteComment");
        data.append("commentId", commentId);

        execute(data, authUrl)
            .then((res) => {
              if (res.data.success) {
                this.showToast("success", res.data.message);

                // Update the comment list by marking it as deleted
                const commentIndex = this.selectedAnnouncement.comments.findIndex(c => c.id === commentId);
                if (commentIndex !== -1) {
                  this.selectedAnnouncement.comments.splice(commentIndex, 1);
                }
              } else {
                this.showToast("error", res.data.message);
              }
            })
            .catch((error) => {
              console.error("API error:", error);
              this.showToast("error", "Network error when deleting comment");
            });
      }
    },

    getAcknowledgmentStats(announcementId) {
      const data = new FormData();
      data.append("function", "getAcknowledgmentStats");
      data.append("announcementId", announcementId);

      execute(data, authUrl)
          .then((res) => {
            if (res.data.success) {
              this.ackStats = res.data.data;

              // Open the stats modal
              this.showModalById('acknowledgeStatsModal');
            } else {
              this.showToast("error", res.data.message);
            }
          })
          .catch((error) => {
            console.error("API error:", error);
            this.showToast("error", "Network error when fetching acknowledgment statistics");
          });
    },

    exportAnnouncements() {
      try {
        // First, determine if we should export filtered data or all data
        this.loading = true;

        // Get all announcements if we need to export everything
        // This is only necessary if the current view is filtered
        const isFiltered = this.filters.departmentId || this.filters.type ||
            this.filters.priority || this.filters.status || this.filters.search;

        if (isFiltered) {
          // Create a copy of current filters but remove pagination
          const exportFilters = {...this.filters, page: 1, limit: 1000};

          const data = new FormData();
          data.append("function", "getAnnouncements");
          data.append("startDate", exportFilters.startDate);
          data.append("endDate", exportFilters.endDate);
          data.append("page", exportFilters.page);
          data.append("limit", exportFilters.limit);

          if (exportFilters.departmentId) {
            data.append("departmentId", exportFilters.departmentId);
          }

          if (exportFilters.type) {
            data.append("type", exportFilters.type);
          }

          if (exportFilters.priority) {
            data.append("priority", exportFilters.priority);
          }

          if (exportFilters.status) {
            data.append("status", exportFilters.status);
          }

          if (exportFilters.search) {
            data.append("search", exportFilters.search);
          }

          // Fetch all data for export
          execute(data, authUrl)
              .then((res) => {
                this.loading = false;
                if (res.data.success) {
                  const announcementsToExport = res.data.data.announcements || [];
                  this.generateCSV(announcementsToExport);
                } else {
                  this.showToast("error", "Failed to fetch data for export: " + res.data.message);
                }
              })
              .catch((error) => {
                this.loading = false;
                console.error("API error:", error);
                this.showToast("error", "Network error when fetching data for export");
              });
        } else {
          // Use the announcements we already have if there are no filters
          this.generateCSV(this.announcements);
          this.loading = false;
        }
      } catch (error) {
        this.loading = false;
        console.error("Export error:", error);
        this.showToast("error", "Error exporting announcements: " + error.message);
      }
    },

    exportAcknowledgmentStats() {
      if (!this.selectedAnnouncement || !this.ackStats) {
        this.showToast("error", "No acknowledgment statistics to export");
        return;
      }

      try {
        // Create new PDF document
        const doc = new jsPDF();

        // Add title
        doc.setFontSize(20);
        doc.setTextColor(40, 40, 40);
        doc.text("Acknowledgment Statistics Report", 105, 20, { align: "center" });

        // Add announcement info
        doc.setFontSize(16);
        doc.text("Announcement Information", 14, 35);
        doc.setDrawColor(220, 220, 220);
        doc.line(14, 37, 196, 37);

        doc.setFontSize(12);
        doc.text(`Title: ${this.selectedAnnouncement.title}`, 14, 45);
        doc.text(`Type: ${this.selectedAnnouncement.announcement_type}`, 14, 52);
        doc.text(`Published: ${this.formatDate(this.selectedAnnouncement.publish_date)}`, 14, 59);
        doc.text(`Status: ${this.selectedAnnouncement.status}`, 14, 66);

        // Add overall stats
        doc.setFontSize(16);
        doc.text("Overall Statistics", 14, 80);
        doc.setDrawColor(220, 220, 220);
        doc.line(14, 82, 196, 82);

        // Create table for overall stats
        autoTable(doc, {
          startY: 90,
          head: [['Metric', 'Count', 'Percentage']],
          body: [
            ['Total Employees', this.ackStats.totalEmployees, '100%'],
            ['Acknowledged', this.ackStats.acknowledgedCount, `${this.ackStats.acknowledgedPercentage}%`],
            ['Pending', this.ackStats.pendingCount, `${this.ackStats.pendingPercentage}%`]
          ],
          theme: 'striped',
          headStyles: { fillColor: [60, 60, 60], textColor: [255, 255, 255] },
          styles: { overflow: 'linebreak', cellPadding: 5 }
        });

        // Add department stats if available
        if (this.ackStats.departmentStats && this.ackStats.departmentStats.length > 0) {
          doc.setFontSize(16);
          doc.text("Department Breakdown", 14, doc.lastAutoTable.finalY + 20);
          doc.setDrawColor(220, 220, 220);
          doc.line(14, doc.lastAutoTable.finalY + 22, 196, doc.lastAutoTable.finalY + 22);

          const deptRows = this.ackStats.departmentStats.map(dept => [
            dept.name,
            dept.totalEmployees,
            dept.acknowledged,
            dept.pending,
            `${dept.percentage}%`
          ]);

          autoTable(doc, {
            startY: doc.lastAutoTable.finalY + 30,
            head: [['Department', 'Total', 'Acknowledged', 'Pending', 'Progress']],
            body: deptRows,
            theme: 'striped',
            headStyles: { fillColor: [60, 60, 60], textColor: [255, 255, 255] },
            styles: { overflow: 'linebreak', cellPadding: 5 }
          });
        }

        // Add recent acknowledgments if available
        if (this.ackStats.recentAcknowledgments && this.ackStats.recentAcknowledgments.length > 0) {
          // Add a new page if we're getting close to the bottom
          if (doc.lastAutoTable.finalY > 200) {
            doc.addPage();
            doc.setFontSize(16);
            doc.text("Recent Acknowledgments", 14, 20);
            doc.setDrawColor(220, 220, 220);
            doc.line(14, 22, 196, 22);

            const ackRows = this.ackStats.recentAcknowledgments.map(ack => [
              ack.employee_name,
              this.formatDateTime(ack.acknowledged_at),
              ack.feedback || 'No feedback provided'
            ]);

            autoTable(doc, {
              startY: 30,
              head: [['Employee', 'Acknowledged At', 'Feedback']],
              body: ackRows,
              theme: 'striped',
              headStyles: { fillColor: [60, 60, 60], textColor: [255, 255, 255] },
              styles: { overflow: 'linebreak', cellPadding: 5 },
              columnStyles: {
                0: { cellWidth: 50 },
                1: { cellWidth: 50 },
                2: { cellWidth: 90 }
              }
            });
          } else {
            doc.setFontSize(16);
            doc.text("Recent Acknowledgments", 14, doc.lastAutoTable.finalY + 20);
            doc.setDrawColor(220, 220, 220);
            doc.line(14, doc.lastAutoTable.finalY + 22, 196, doc.lastAutoTable.finalY + 22);

            const ackRows = this.ackStats.recentAcknowledgments.map(ack => [
              ack.employee_name,
              this.formatDateTime(ack.acknowledged_at),
              ack.feedback || 'No feedback provided'
            ]);

            autoTable(doc, {
              startY: doc.lastAutoTable.finalY + 30,
              head: [['Employee', 'Acknowledged At', 'Feedback']],
              body: ackRows,
              theme: 'striped',
              headStyles: { fillColor: [60, 60, 60], textColor: [255, 255, 255] },
              styles: { overflow: 'linebreak', cellPadding: 5 },
              columnStyles: {
                0: { cellWidth: 50 },
                1: { cellWidth: 50 },
                2: { cellWidth: 90 }
              }
            });
          }
        }

        // Add footer with page numbers
        const pageCount = doc.internal.getNumberOfPages();
        for (let i = 1; i <= pageCount; i++) {
          doc.setPage(i);
          doc.setFontSize(8);
          doc.setTextColor(100, 100, 100);

          // Current date at bottom left
          doc.text(
              `Generated: ${new Date().toLocaleString()}`,
              14,
              doc.internal.pageSize.height - 10
          );

          // Page numbers at bottom right
          doc.text(
              `Page ${i} of ${pageCount}`,
              doc.internal.pageSize.width - 14,
              doc.internal.pageSize.height - 10,
              { align: 'right' }
          );
        }

        // Save the PDF file
        doc.save(`acknowledgment-stats-${this.selectedAnnouncement.id}.pdf`);
        this.showToast("success", "Acknowledgment statistics exported as PDF successfully");
      } catch (error) {
        console.error("PDF generation error:", error);
        this.showToast("error", "Error generating PDF: " + error.message);
      }
    },

    generateCSV(announcementsData) {
      if (!announcementsData || announcementsData.length === 0) {
        this.showToast("warning", "No announcements data to export");
        return;
      }

      // Define the CSV columns
      const csvColumns = [
        "ID",
        "Title",
        "Type",
        "Priority",
        "Status",
        "Publish Date",
        "Expiry Date",
        "Creator",
        "Created At",
        "Views",
        "Requires Acknowledgment",
        "Content"
      ];

      // Convert announcements data to CSV rows
      const csvRows = announcementsData.map(announcement => [
        announcement.id || "",
        announcement.title || "",
        announcement.announcement_type || "",
        announcement.priority || "",
        announcement.status || "",
        announcement.publish_date ? this.formatDate(announcement.publish_date) : "",
        announcement.expiry_date ? this.formatDate(announcement.expiry_date) : "",
        announcement.created_by || "",
        announcement.created_at ? this.formatDateTime(announcement.created_at) : "",
        announcement.views || "0",
        announcement.require_acknowledgment ? "Yes" : "No",
        announcement.content ? `"${announcement.content.replace(/"/g, '""')}"` : ""
      ]);

      // Create CSV content
      let csvContent = [csvColumns, ...csvRows]
          .map(row => row.join(","))
          .join("\n");

      // Create and download the CSV file
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");

      const today = new Date();
      const formattedDate = today.toISOString().split("T")[0];
      const filename = `announcements-${formattedDate}.csv`;

      link.setAttribute("href", url);
      link.setAttribute("download", filename);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);

      this.showToast("success", "Announcements data exported successfully");
    },

    // Permission checks
    canEditAnnouncement(announcement) {
      // You can add additional permission checks here if needed
      return announcement.status !== 'Archived';
    },

    canDeleteComment(comment) {
      try {
        // Get current user's ID
        const token = localStorage.getItem('token');

        // Check if token exists
        if (!token) {
          console.warn('No authentication token found');
          return false; // No token means no permission
        }

        const payload = JSON.parse(atob(token.split('.')[1]));
        const userId = payload.username;

        // Check if user is the comment author or an admin
        return comment.employee_id === userId || this.isUserAdmin();
      } catch (error) {
        console.error('Error checking comment deletion permission:', error);
        return false; // If there's any error, deny permission
      }
    },



    isUserAdmin() {
      try {
        // Get current user's roles
        const token = localStorage.getItem('token');

        // Check if token exists
        if (!token) {
          console.warn('No authentication token found');
          return false;
        }

        const payload = JSON.parse(atob(token.split('.')[1]));
        const roles = payload.roles || [];

        return roles.includes('admin') || roles.includes('hr');
      } catch (error) {
        console.error('Error checking admin status:', error);
        return false;
      }
    },
    // Utility methods
    formatDate(dateString) {
      if (!dateString) return 'N/A';

      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    formatDateTime(dateTimeString) {
      if (!dateTimeString) return 'N/A';

      const date = new Date(dateTimeString);
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
      });
    },

    formatDateMonth(dateString) {
      if (!dateString) return '';

      const date = new Date(dateString);
      return date.toLocaleDateString('en-US', { month: 'short' });
    },

    formatDateDay(dateString) {
      if (!dateString) return '';

      const date = new Date(dateString);
      return date.getDate();
    },

    formatFileSize(bytes) {
      if (!bytes || bytes === 0) return '0 Bytes';

      const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB'];
      const i = Math.floor(Math.log(bytes) / Math.log(1024));
      return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i];
    },

    formatAnnouncementContent(content) {
      if (!content) return '';

      // Convert URLs to clickable links
      const urlPattern = /(https?:\/\/[^\s]+)/g;
      let formattedContent = content.replace(urlPattern, url => `<a href="${url}" target="_blank">${url}</a>`);

      // Convert line breaks to <br> tags
      formattedContent = formattedContent.replace(/(?:\r\n|\r|\n)/g, '<br>');

      return formattedContent;
    },

    getAnnouncementTypeColor(type) {
      if (!type) return 'bg-secondary';

      const typeMap = {
        'General': 'bg-primary',
        'Policy Update': 'bg-info',
        'Event': 'bg-success',
        'Emergency': 'bg-danger',
        'HR Announcement': 'bg-warning',
        'IT Notice': 'bg-dark',
        'Training': 'bg-info',
        'Holiday': 'bg-success',
        'Maintenance': 'bg-warning',
        'Other': 'bg-secondary'
      };

      return typeMap[type] || 'bg-secondary';
    },

    getPriorityBadgeClass(priority) {
      if (!priority) return 'bg-secondary';

      const priorityMap = {
        'High': 'bg-danger',
        'Medium': 'bg-warning',
        'Low': 'bg-info'
      };

      return priorityMap[priority] || 'bg-secondary';
    },

    getStatusBadgeClass(status) {
      if (!status) return 'bg-secondary';

      const statusMap = {
        'Active': 'bg-success',
        'Draft': 'bg-warning',
        'Archived': 'bg-secondary'
      };

      return statusMap[status] || 'bg-secondary';
    },

    getAnnouncementTypePercentage(type) {
      if (!this.metrics.typeCounts || !this.metrics.totalAnnouncements) return 0;

      const count = this.metrics.typeCounts[type] || 0;
      const total = Object.values(this.metrics.typeCounts).reduce((sum, count) => sum + count, 0);
      return ((count / total) * 100).toFixed(1);
    },

    getActivityTypeClass(type) {
      if (!type) return 'bg-secondary text-white';

      const typeMap = {
        'create': 'bg-primary text-white',
        'update': 'bg-info text-white',
        'acknowledge': 'bg-success text-white',
        'comment': 'bg-purple text-white',
        'comment_delete': 'bg-danger text-white',
        'archive': 'bg-warning text-white'
      };

      return typeMap[type] || 'bg-secondary text-white';
    },

    getActivityTypeIcon(type) {
      if (!type) return 'mdi-help-circle';

      const typeMap = {
        'create': 'mdi-plus-circle',
        'update': 'mdi-pencil',
        'acknowledge': 'mdi-check-circle',
        'comment': 'mdi-comment-text',
        'comment_delete': 'mdi-comment-remove',
        'archive': 'mdi-archive'
      };

      return typeMap[type] || 'mdi-help-circle';
    },

    getDepartmentName(departmentId) {
      const department = this.departments.find(d => d.id === departmentId);
      return department ? department.departmentName : departmentId;
    },

    getInitials(name) {
      if (!name) return '';

      return name
          .split(' ')
          .map(word => word.charAt(0))
          .join('')
          .toUpperCase()
          .substring(0, 2);
    },

    truncateText(text, maxLength) {
      if (!text) return '';

      if (text.length <= maxLength) return text;
      return text.substring(0, maxLength) + '...';
    },

    showToast(type, message) {
      if (type === 'success') {
        toast.success(message);
      } else if (type === 'error') {
        toast.error(message);
      } else if (type === 'warning') {
        toast.warning(message);
      } else {
        toast.info(message);
      }
    }
  },
  watch: {
    currentView: function() {
      this.filters.page = 1;
      this.filters.status = ""; // Reset status filter when switching views
      this.getAnnouncements();
    },
    'filters.departmentId': function() {
      this.filters.page = 1;
      this.getAnnouncements();
    },
    'filters.type': function() {
      this.filters.page = 1;
      this.getAnnouncements();
    },
    'filters.priority': function() {
      this.filters.page = 1;
      this.getAnnouncements();
    },
    'filters.status': function() {
      this.filters.page = 1;
      this.getAnnouncements();
    },
    'filters.search': _.debounce(function() {
      this.filters.page = 1;
      this.getAnnouncements();
    }, 500),
    'filters.startDate': function() {
      this.getAnnouncements();
    },
    'filters.endDate': function() {
      this.getAnnouncements();
    },
    'announcementForm.targetAudience': function(newValue) {
      if (newValue !== 'Specific Departments') {
        this.announcementForm.departments = [];
      }
    }
  }
};
</script>

<style scoped>
/* ── Top Toolbar ── */
.ann-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #fff;
  border-radius: 14px;
  border: 1px solid #e2e8f0;
  padding: 8px 12px;
  margin-bottom: 20px;
  box-shadow: 0 2px 8px rgba(0,0,0,0.05);
  flex-wrap: wrap;
  gap: 10px;
}

.ann-view-tabs {
  display: flex;
  gap: 4px;
  background: #f1f5f9;
  border-radius: 10px;
  padding: 4px;
}

.ann-tab {
  display: flex;
  align-items: center;
  gap: 7px;
  padding: 8px 18px;
  border-radius: 8px;
  border: none;
  background: transparent;
  color: #64748b;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.18s;
  white-space: nowrap;
}
.ann-tab i { font-size: 16px; }
.ann-tab:hover { background: #e2e8f0; color: #1e293b; }
.ann-tab-active {
  background: #fff !important;
  color: #059669 !important;
  box-shadow: 0 2px 8px rgba(0,0,0,0.08);
}

.ann-tab-count {
  background: #059669;
  color: #fff;
  font-size: 10px;
  font-weight: 700;
  padding: 1px 6px;
  border-radius: 20px;
  min-width: 20px;
  text-align: center;
}
.ann-tab-count-muted { background: #94a3b8; }

.ann-toolbar-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.ann-tool-btn {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 8px 16px;
  border-radius: 8px;
  border: 1px solid transparent;
  font-size: 13px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.15s;
  white-space: nowrap;
}
.ann-tool-btn i { font-size: 16px; }

.ann-tool-refresh {
  background: #f8fafc;
  color: #475569;
  border-color: #e2e8f0;
}
.ann-tool-refresh:hover { background: #e2e8f0; color: #1e293b; }

.ann-tool-export {
  background: #f0fdf4;
  color: #16a34a;
  border-color: #bbf7d0;
}
.ann-tool-export:hover { background: #16a34a; color: #fff; border-color: #16a34a; }

.ann-tool-create {
  background: linear-gradient(135deg, #059669 0%, #0d9488 100%);
  color: #fff;
  border-color: transparent;
  box-shadow: 0 2px 8px rgba(5,150,105,0.3);
}
.ann-tool-create:hover {
  background: linear-gradient(135deg, #047857 0%, #0f766e 100%);
  box-shadow: 0 4px 12px rgba(5,150,105,0.4);
  transform: translateY(-1px);
}

/* ── Announcement List ── */
.ann-list { display: flex; flex-direction: column; gap: 12px; }

.ann-item {
  display: flex;
  background: #fff;
  border-radius: 12px;
  border: 1px solid #e8edf3;
  overflow: hidden;
  transition: box-shadow 0.2s, transform 0.2s;
}
.ann-item:hover { box-shadow: 0 4px 20px rgba(0,0,0,0.09); transform: translateY(-1px); }

.ann-item-accent { width: 5px; flex-shrink: 0; }
.ann-accent-high { background: #ef4444; }
.ann-accent-critical { background: #7c3aed; }
.ann-accent-medium { background: #f59e0b; }
.ann-accent-low { background: #10b981; }
.ann-accent-normal { background: #3b82f6; }

.ann-item-body { flex: 1; padding: 16px 20px; min-width: 0; }
.ann-item-top { display: flex; gap: 14px; align-items: flex-start; }

.ann-item-icon {
  width: 44px; height: 44px; border-radius: 10px;
  display: flex; align-items: center; justify-content: center;
  flex-shrink: 0; color: #fff; font-size: 20px;
}

.ann-item-main { flex: 1; min-width: 0; }
.ann-item-title-row { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-bottom: 6px; }
.ann-item-title { margin: 0; font-size: 15px; font-weight: 700; color: #1e293b; }
.ann-item-badges { display: flex; flex-wrap: wrap; gap: 5px; }
.ann-item-excerpt { margin: 0 0 8px; font-size: 13px; color: #64748b; line-height: 1.5; }
.ann-item-meta { display: flex; flex-wrap: wrap; gap: 14px; font-size: 12px; color: #94a3b8; }
.ann-item-meta span { display: flex; align-items: center; gap: 4px; }
.ann-item-meta i { font-size: 13px; }

.ann-item-actions { display: flex; flex-direction: column; gap: 6px; flex-shrink: 0; padding-left: 8px; }

.ann-action-btn {
  display: flex; align-items: center; gap: 5px;
  padding: 6px 12px; border-radius: 7px; border: none;
  font-size: 12px; font-weight: 600; cursor: pointer;
  transition: all 0.15s; white-space: nowrap;
}
.ann-action-view  { background: #eff6ff; color: #2563eb; }
.ann-action-view:hover  { background: #2563eb; color: #fff; }
.ann-action-edit  { background: #f0fdf4; color: #16a34a; }
.ann-action-edit:hover  { background: #16a34a; color: #fff; }
.ann-action-stats { background: #fefce8; color: #ca8a04; }
.ann-action-stats:hover { background: #ca8a04; color: #fff; }
.ann-action-archive { background: #fff7ed; color: #ea580c; }
.ann-action-archive:hover { background: #ea580c; color: #fff; }
.ann-action-restore { background: #f0fdf4; color: #059669; }
.ann-action-restore:hover { background: #059669; color: #fff; }

/* ── Badges ── */
.ann-badge {
  display: inline-flex; align-items: center; gap: 3px;
  padding: 3px 8px; border-radius: 20px;
  font-size: 11px; font-weight: 600; letter-spacing: 0.3px;
}
.ann-badge-ack { background: #ede9fe; color: #7c3aed; }

/* ── Modal ── */
.ann-modal-content { border: none; border-radius: 16px; overflow: hidden; box-shadow: 0 20px 60px rgba(0,0,0,0.18); }

.ann-modal-header {
  background: linear-gradient(135deg, #1e293b 0%, #334155 100%);
  padding: 20px 24px;
}
.ann-modal-header-inner { display: flex; align-items: flex-start; justify-content: space-between; gap: 16px; }
.ann-modal-title-wrap { display: flex; align-items: center; gap: 14px; }
.ann-modal-icon {
  width: 48px; height: 48px; border-radius: 12px;
  display: flex; align-items: center; justify-content: center;
  color: #fff; font-size: 22px; flex-shrink: 0;
}
.ann-modal-title { margin: 0; font-size: 18px; font-weight: 700; color: #fff; line-height: 1.3; }
.ann-modal-badges { display: flex; flex-wrap: wrap; gap: 6px; margin-top: 6px; }
.ann-modal-close {
  background: rgba(255,255,255,0.12); border: none; color: #fff;
  width: 34px; height: 34px; border-radius: 8px; font-size: 20px;
  display: flex; align-items: center; justify-content: center;
  cursor: pointer; flex-shrink: 0; transition: background 0.15s;
}
.ann-modal-close:hover { background: rgba(255,255,255,0.25); }

.ann-modal-body { padding: 0; }

/* Meta strip */
.ann-detail-meta-strip {
  display: flex; flex-wrap: wrap; gap: 0;
  background: #f8fafc; border-bottom: 1px solid #e2e8f0;
}
.ann-detail-meta-item {
  display: flex; align-items: center; gap: 10px;
  padding: 14px 20px; border-right: 1px solid #e2e8f0;
  flex: 1; min-width: 140px;
}
.ann-detail-meta-item i { font-size: 20px; color: #059669; flex-shrink: 0; }
.ann-meta-label { display: block; font-size: 11px; color: #94a3b8; font-weight: 600; text-transform: uppercase; letter-spacing: 0.5px; }
.ann-meta-value { display: block; font-size: 13px; color: #1e293b; font-weight: 600; margin-top: 1px; }

/* Sections */
.ann-detail-section { border-bottom: 1px solid #f1f5f9; }
.ann-detail-section:last-child { border-bottom: none; }
.ann-detail-section-header {
  display: flex; align-items: center; justify-content: space-between;
  padding: 14px 24px; background: #f8fafc;
  font-size: 13px; font-weight: 700; color: #475569;
  border-bottom: 1px solid #e2e8f0; gap: 8px;
}
.ann-detail-section-header i { font-size: 16px; color: #059669; }
.ann-detail-section-body { padding: 20px 24px; }
.ann-detail-content-body { line-height: 1.8; color: #334155; font-size: 14px; }
.ann-detail-content-body :deep(p) { margin-bottom: 1rem; }
.ann-detail-content-body :deep(ul), .ann-detail-content-body :deep(ol) { padding-left: 1.5rem; margin-bottom: 1rem; }

.ann-detail-row { display: flex; gap: 0; border-bottom: 1px solid #f1f5f9; }
.ann-detail-half { flex: 1; border-bottom: none; }
.ann-detail-half:first-child { border-right: 1px solid #f1f5f9; }

/* Audience */
.ann-audience-label { font-size: 14px; font-weight: 600; color: #1e293b; margin-bottom: 10px; }
.ann-dept-tags { display: flex; flex-wrap: wrap; gap: 6px; }
.ann-dept-tag { background: #eff6ff; color: #2563eb; padding: 4px 10px; border-radius: 6px; font-size: 12px; font-weight: 600; display: flex; align-items: center; gap: 4px; }
.ann-ack-notice { margin-top: 12px; color: #7c3aed; font-size: 13px; font-weight: 600; display: flex; align-items: center; gap: 6px; }

/* Attachments */
.ann-attach-list { display: flex; flex-direction: column; gap: 8px; }
.ann-attach-item { display: flex; align-items: center; gap: 12px; padding: 10px 14px; background: #f8fafc; border-radius: 8px; border: 1px solid #e2e8f0; }
.ann-attach-icon { width: 36px; height: 36px; background: #eff6ff; border-radius: 8px; display: flex; align-items: center; justify-content: center; color: #2563eb; font-size: 18px; flex-shrink: 0; }
.ann-attach-info { flex: 1; min-width: 0; }
.ann-attach-name { display: block; font-size: 13px; font-weight: 600; color: #1e293b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ann-attach-size { font-size: 11px; color: #94a3b8; }
.ann-attach-download { width: 32px; height: 32px; background: #2563eb; color: #fff; border-radius: 7px; display: flex; align-items: center; justify-content: center; font-size: 16px; text-decoration: none; flex-shrink: 0; transition: background 0.15s; }
.ann-attach-download:hover { background: #1d4ed8; }

/* Empty small */
.ann-empty-small { display: flex; align-items: center; gap: 8px; color: #94a3b8; font-size: 13px; padding: 8px 0; }
.ann-empty-small i { font-size: 20px; }

/* Count badge */
.ann-count-badge { background: #2563eb; color: #fff; padding: 2px 8px; border-radius: 20px; font-size: 11px; font-weight: 700; }
.ann-count-success { background: #059669; }

/* Comments */
.ann-comments { display: flex; flex-direction: column; gap: 12px; margin-bottom: 16px; }
.ann-comment { display: flex; gap: 12px; }
.ann-comment-avatar { width: 36px; height: 36px; border-radius: 50%; background: linear-gradient(135deg, #059669, #2563eb); color: #fff; display: flex; align-items: center; justify-content: center; font-size: 12px; font-weight: 700; flex-shrink: 0; }
.ann-comment-body { flex: 1; background: #f8fafc; border-radius: 10px; padding: 10px 14px; border: 1px solid #e2e8f0; }
.ann-comment-header { display: flex; align-items: center; gap: 8px; margin-bottom: 4px; }
.ann-comment-author { font-size: 13px; font-weight: 700; color: #1e293b; }
.ann-comment-time { font-size: 11px; color: #94a3b8; flex: 1; }
.ann-comment-delete { background: none; border: none; color: #ef4444; cursor: pointer; padding: 0; font-size: 15px; opacity: 0.6; }
.ann-comment-delete:hover { opacity: 1; }
.ann-comment-text { margin: 0; font-size: 13px; color: #475569; line-height: 1.5; }
.ann-comment-form { display: flex; gap: 10px; align-items: flex-end; padding-top: 12px; border-top: 1px solid #e2e8f0; }
.ann-comment-input { flex: 1; border: 1px solid #e2e8f0; border-radius: 8px; padding: 8px 12px; font-size: 13px; resize: none; outline: none; }
.ann-comment-input:focus { border-color: #059669; }
.ann-comment-post { background: #059669; color: #fff; border: none; border-radius: 8px; padding: 8px 16px; font-size: 13px; font-weight: 600; cursor: pointer; display: flex; align-items: center; gap: 5px; white-space: nowrap; }
.ann-comment-post:disabled { opacity: 0.5; cursor: not-allowed; }

/* Timeline */
.ann-timeline { display: flex; flex-direction: column; gap: 0; }
.ann-timeline-item { display: flex; gap: 14px; padding-bottom: 20px; position: relative; }
.ann-timeline-item:not(:last-child)::before { content: ''; position: absolute; left: 17px; top: 36px; width: 2px; height: calc(100% - 20px); background: #e2e8f0; }
.ann-timeline-dot { width: 36px; height: 36px; border-radius: 50%; display: flex; align-items: center; justify-content: center; font-size: 16px; color: #fff; flex-shrink: 0; }
.ann-timeline-content { flex: 1; padding-top: 4px; }
.ann-timeline-header { display: flex; justify-content: space-between; align-items: center; margin-bottom: 4px; }
.ann-timeline-title { font-size: 13px; font-weight: 700; color: #1e293b; }
.ann-timeline-time { font-size: 11px; color: #94a3b8; }
.ann-timeline-desc { margin: 0 0 2px; font-size: 13px; color: #64748b; }
.ann-timeline-by { font-size: 11px; color: #94a3b8; }

/* Table */
.ann-table { width: 100%; border-collapse: collapse; font-size: 13px; }
.ann-table th { background: #f8fafc; padding: 10px 14px; text-align: left; font-weight: 700; color: #475569; border-bottom: 2px solid #e2e8f0; }
.ann-table td { padding: 10px 14px; border-bottom: 1px solid #f1f5f9; color: #334155; }
.ann-table tr:last-child td { border-bottom: none; }
.ann-table tr:hover td { background: #f8fafc; }

/* Modal footer */
.ann-modal-footer {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 24px; background: #f8fafc; border-top: 1px solid #e2e8f0;
  gap: 12px;
}
.ann-footer-right { display: flex; gap: 10px; }
.ann-footer-btn {
  display: flex; align-items: center; gap: 7px;
  padding: 9px 20px; border-radius: 8px; border: none;
  font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.15s;
}
.ann-footer-close { background: #f1f5f9; color: #475569; }
.ann-footer-close:hover { background: #e2e8f0; }
.ann-footer-edit { background: #eff6ff; color: #2563eb; border: 1px solid #bfdbfe; }
.ann-footer-edit:hover { background: #2563eb; color: #fff; border-color: #2563eb; }
.ann-footer-ack { background: #059669; color: #fff; }
.ann-footer-ack:hover { background: #047857; }

/* Department selection scrollable area */
.department-selection { max-height: 200px; overflow-y: auto; }

/* ── Create/Edit Form Modal ── */
.ann-form-modal { border: none; border-radius: 16px; overflow: hidden; box-shadow: 0 20px 60px rgba(0,0,0,0.18); }

.ann-form-header {
  background: linear-gradient(135deg, #059669 0%, #0d9488 100%);
  padding: 20px 24px;
  display: flex; align-items: center; justify-content: space-between; gap: 16px;
}
.ann-form-header-inner { display: flex; align-items: center; gap: 14px; }
.ann-form-header-icon {
  width: 48px; height: 48px; border-radius: 12px;
  background: rgba(255,255,255,0.2);
  display: flex; align-items: center; justify-content: center;
  font-size: 22px; color: #fff; flex-shrink: 0;
}
.ann-form-title { margin: 0; font-size: 18px; font-weight: 700; color: #fff; }
.ann-form-subtitle { margin: 3px 0 0; font-size: 12px; color: rgba(255,255,255,0.75); }

.ann-form-body { padding: 0; background: #f8fafc; }

/* Sections */
.ann-form-section {
  background: #fff; margin: 16px; border-radius: 12px;
  border: 1px solid #e2e8f0; overflow: hidden;
}
.ann-form-section-label {
  display: flex; align-items: center; gap: 8px;
  padding: 12px 18px; background: #f8fafc; border-bottom: 1px solid #e2e8f0;
  font-size: 12px; font-weight: 700; color: #475569; text-transform: uppercase; letter-spacing: 0.5px;
}
.ann-form-section-label i { font-size: 15px; color: #059669; }
.ann-form-section-label .ann-required { color: #ef4444; font-size: 13px; }
.ann-form-section-label .ann-hint { color: #94a3b8; font-weight: 500; text-transform: none; letter-spacing: 0; font-size: 11px; }

/* Grid */
.ann-form-grid { display: grid; grid-template-columns: 1fr 1fr; gap: 16px; padding: 18px; }
.ann-field-full { grid-column: span 2; }

/* Fields */
.ann-field { display: flex; flex-direction: column; gap: 6px; }
.ann-label { font-size: 12px; font-weight: 700; color: #374151; display: flex; align-items: center; gap: 4px; }
.ann-required { color: #ef4444; }
.ann-hint { color: #94a3b8; font-weight: 500; font-size: 11px; }
.ann-help { font-size: 11px; color: #94a3b8; margin-top: 2px; }

.ann-input {
  width: 100%; padding: 9px 12px; border: 1.5px solid #e2e8f0; border-radius: 8px;
  font-size: 13px; color: #1e293b; background: #fff; outline: none; transition: border-color 0.15s;
  box-sizing: border-box;
}
.ann-input:focus { border-color: #059669; box-shadow: 0 0 0 3px rgba(5,150,105,0.1); }

.ann-input-icon-wrap { position: relative; }
.ann-input-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); font-size: 16px; color: #94a3b8; pointer-events: none; }
.ann-input-icon-pad { padding-left: 34px; }

.ann-select-wrap { position: relative; }
.ann-select-icon { position: absolute; left: 10px; top: 50%; transform: translateY(-50%); font-size: 16px; color: #94a3b8; pointer-events: none; z-index: 1; }
.ann-select {
  width: 100%; padding: 9px 12px 9px 34px; border: 1.5px solid #e2e8f0; border-radius: 8px;
  font-size: 13px; color: #1e293b; background: #fff; outline: none; cursor: pointer;
  appearance: none; transition: border-color 0.15s;
}
.ann-select:focus { border-color: #059669; box-shadow: 0 0 0 3px rgba(5,150,105,0.1); }

/* Priority selector */
.ann-priority-group { display: flex; gap: 8px; }
.ann-priority-opt {
  flex: 1; display: flex; align-items: center; justify-content: center; gap: 5px;
  padding: 8px 10px; border-radius: 8px; border: 1.5px solid #e2e8f0;
  font-size: 12px; font-weight: 600; cursor: pointer; transition: all 0.15s;
  color: #64748b; background: #f8fafc;
}
.ann-priority-opt:hover { border-color: #cbd5e1; background: #f1f5f9; }
.ann-priority-high { background: #fef2f2 !important; border-color: #ef4444 !important; color: #ef4444 !important; }
.ann-priority-medium { background: #fffbeb !important; border-color: #f59e0b !important; color: #d97706 !important; }
.ann-priority-low { background: #f0fdf4 !important; border-color: #10b981 !important; color: #059669 !important; }

/* Audience selector */
.ann-audience-group { display: flex; gap: 12px; padding: 18px; }
.ann-audience-opt {
  flex: 1; display: flex; align-items: center; gap: 14px;
  padding: 14px 16px; border-radius: 10px; border: 1.5px solid #e2e8f0;
  cursor: pointer; transition: all 0.15s; background: #f8fafc;
}
.ann-audience-opt:hover { border-color: #059669; background: #f0fdf4; }
.ann-audience-active { border-color: #059669 !important; background: #f0fdf4 !important; }
.ann-audience-icon {
  width: 40px; height: 40px; border-radius: 10px; background: #e2e8f0;
  display: flex; align-items: center; justify-content: center; font-size: 20px; color: #64748b; flex-shrink: 0;
}
.ann-audience-active .ann-audience-icon { background: #d1fae5; color: #059669; }
.ann-audience-title { display: block; font-size: 13px; font-weight: 700; color: #1e293b; }
.ann-audience-desc { display: block; font-size: 11px; color: #94a3b8; margin-top: 2px; }
.ann-audience-check { margin-left: auto; font-size: 20px; color: #e2e8f0; }
.ann-audience-active .ann-audience-check { color: #059669; }

/* Department checkboxes */
.ann-dept-grid { display: flex; flex-wrap: wrap; gap: 8px; padding: 0 18px 18px; }
.ann-dept-check {
  display: flex; align-items: center; gap: 6px;
  padding: 6px 12px; border-radius: 7px; border: 1.5px solid #e2e8f0;
  font-size: 12px; font-weight: 600; color: #475569; cursor: pointer;
  background: #f8fafc; transition: all 0.15s;
}
.ann-dept-check:hover { border-color: #059669; color: #059669; }
.ann-dept-checked { border-color: #059669 !important; background: #f0fdf4 !important; color: #059669 !important; }
.ann-dept-check i { font-size: 15px; }

/* Textarea */
.ann-textarea {
  width: 100%; padding: 12px 14px; border: 1.5px solid #e2e8f0; border-radius: 8px;
  font-size: 13px; color: #1e293b; background: #fff; outline: none; resize: vertical;
  transition: border-color 0.15s; margin: 0 18px; width: calc(100% - 36px);
  box-sizing: border-box; display: block;
}
.ann-textarea:focus { border-color: #059669; box-shadow: 0 0 0 3px rgba(5,150,105,0.1); }
.ann-form-section .ann-help { padding: 6px 18px 14px; display: block; }

/* Status selector */
.ann-status-group { display: flex; gap: 10px; }
.ann-status-opt {
  flex: 1; display: flex; align-items: center; justify-content: center; gap: 7px;
  padding: 10px 14px; border-radius: 8px; border: 1.5px solid #e2e8f0;
  font-size: 13px; font-weight: 600; cursor: pointer; transition: all 0.15s;
  color: #64748b; background: #f8fafc;
}
.ann-status-opt:hover { border-color: #cbd5e1; }
.ann-status-active { background: #f0fdf4 !important; border-color: #059669 !important; color: #059669 !important; }
.ann-status-draft { background: #fffbeb !important; border-color: #f59e0b !important; color: #d97706 !important; }

/* Toggle */
.ann-toggle-wrap {
  display: flex; align-items: center; justify-content: space-between;
  padding: 12px 14px; border-radius: 10px; border: 1.5px solid #e2e8f0;
  cursor: pointer; transition: all 0.15s; background: #f8fafc;
}
.ann-toggle-wrap:hover { border-color: #059669; }
.ann-toggle-on { border-color: #059669 !important; background: #f0fdf4 !important; }
.ann-toggle-info { display: flex; align-items: center; gap: 10px; }
.ann-toggle-info i { font-size: 20px; color: #94a3b8; }
.ann-toggle-on .ann-toggle-info i { color: #059669; }
.ann-toggle-title { display: block; font-size: 13px; font-weight: 700; color: #1e293b; }
.ann-toggle-desc { display: block; font-size: 11px; color: #94a3b8; }
.ann-toggle-switch {
  width: 44px; height: 24px; border-radius: 12px; background: #e2e8f0;
  position: relative; transition: background 0.2s; flex-shrink: 0;
}
.ann-toggle-on .ann-toggle-switch { background: #059669; }
.ann-toggle-knob {
  position: absolute; top: 3px; left: 3px;
  width: 18px; height: 18px; border-radius: 50%; background: #fff;
  transition: transform 0.2s; box-shadow: 0 1px 4px rgba(0,0,0,0.2);
}
.ann-toggle-on .ann-toggle-knob { transform: translateX(20px); }

/* File upload */
.ann-file-drop {
  display: flex; flex-direction: column; align-items: center; justify-content: center;
  gap: 6px; padding: 28px 20px; margin: 0 18px 14px;
  border: 2px dashed #cbd5e1; border-radius: 10px; cursor: pointer;
  background: #f8fafc; transition: all 0.15s;
}
.ann-file-drop:hover { border-color: #059669; background: #f0fdf4; }
.ann-file-icon { font-size: 32px; color: #94a3b8; }
.ann-file-drop:hover .ann-file-icon { color: #059669; }
.ann-file-text { font-size: 13px; font-weight: 600; color: #475569; }
.ann-file-hint { font-size: 11px; color: #94a3b8; }

.ann-existing-files { display: flex; flex-direction: column; gap: 6px; padding: 0 18px 16px; }
.ann-file-item {
  display: flex; align-items: center; gap: 10px;
  padding: 8px 12px; background: #f8fafc; border-radius: 8px; border: 1px solid #e2e8f0;
}
.ann-file-item-icon { width: 30px; height: 30px; background: #eff6ff; border-radius: 6px; display: flex; align-items: center; justify-content: center; color: #2563eb; font-size: 16px; flex-shrink: 0; }
.ann-file-item-name { flex: 1; font-size: 12px; font-weight: 600; color: #1e293b; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
.ann-file-remove { background: none; border: none; color: #ef4444; cursor: pointer; font-size: 18px; padding: 0; opacity: 0.7; }
.ann-file-remove:hover { opacity: 1; }

/* Form footer */
.ann-form-footer {
  display: flex; align-items: center; justify-content: space-between;
  padding: 16px 24px; background: #fff; border-top: 1px solid #e2e8f0; gap: 12px;
}
.ann-form-submit {
  background: linear-gradient(135deg, #059669, #0d9488);
  color: #fff; border-color: transparent;
  box-shadow: 0 2px 8px rgba(5,150,105,0.3);
  padding: 10px 24px; font-size: 14px;
}
.ann-form-submit:hover:not(:disabled) { background: linear-gradient(135deg, #047857, #0f766e); transform: translateY(-1px); }
.ann-form-submit:disabled { opacity: 0.6; cursor: not-allowed; }
.ann-btn-spinner {
  width: 14px; height: 14px; border: 2px solid rgba(255,255,255,0.4);
  border-top-color: #fff; border-radius: 50%;
  animation: ann-spin 0.7s linear infinite; display: inline-block;
}
@keyframes ann-spin { to { transform: rotate(360deg); } }

@media (max-width: 640px) {
  .ann-form-grid { grid-template-columns: 1fr; }
  .ann-field-full { grid-column: span 1; }
  .ann-audience-group { flex-direction: column; }
  .ann-priority-group { flex-wrap: wrap; }
}
</style>

<style src="@/assets/css/hr-page-shared.css"></style>